import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Slide } from "react-slideshow-image";
import "./Prediction.scss";
import { Button, Input } from "reactstrap";
import "react-slideshow-image/dist/styles.css";
import { getCokkie } from "../../../views/login/Login";
import ErrorScreen from "../../error/ErrorScreen";
import useFetch from "../../useFetch";
import AlertComponent from "../../alert/AlertComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faClose, faLightbulb, faSpinner, faSquare, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import Canvas from "./Canvas";
import { ReactStrapModal } from "../../reactstrap_modal/ReactStrapModal";
import getDataFromApi from "../../getDataFromApi";
import AudioPlayer from "./AudioPlayer";
import Confirm from "../../confirm/Confirm";
import ModalComponent from "../ModalComponent";
import { Skeleton } from "@mui/material";
import MultiImage from "./MultiImage";
import EvaluationVideo from "../../../views/project/models/EvaluationVideo";
import { ActionsIcon, AiInsightsIcon, BackIcon, CustomisePlanIcon, EyeIcon, EyeOffIcon, ImagesIcon, LabelsIcon, PredictionsIcon } from "../../../views/Common/SvgIcons/WorkflowIcons";
import useImageZoom from "./useImageZoom";
import { isDicomSource, loadRenderableImageSource, useRenderableImageSource } from "./dicomImage";

const TRAILING_HEX_COLOR = /#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{4}|[0-9a-fA-F]{3})$/;
const HEX_COLOR = /^#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{4}|[0-9a-fA-F]{3})$/;

const PredictionThumbnail = ({ src, ...props }) => {
  const { src: renderableSrc, loading, error } = useRenderableImageSource(src);

  return (
    <img
      {...props}
      alt={props.alt || ""}
      src={renderableSrc || undefined}
      aria-busy={loading || undefined}
      title={error ? "Unable to display this DICOM image" : props.title}
    />
  );
};

const PredictionToggle = ({ checked, disabled = false, label, onChange }) => (
  <label
    className={`toggle-switch ${checked ? "is-active" : "is-inactive"}${
      disabled ? " is-disabled" : ""
    }`}
    style={{ cursor: disabled ? "not-allowed" : "pointer", flexShrink: 0 }}
  >
    <input
      type="checkbox"
      aria-label={label}
      aria-checked={checked}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
    <span className="slider" aria-hidden="true" />
  </label>
);

const normalizeLabelValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");

const toLabelOption = (item) => {
  if (!item) return null;
  if (typeof item === "string" || typeof item === "number") {
    return { name: String(item) };
  }
  if (typeof item === "object") {
    const name = item.name ?? item.label ?? item.class_name ?? item.class;
    if (!name) return null;
    return {
      name: String(name),
      colour: item.colour ?? item.color,
    };
  }
  return null;
};

const flattenLabelOptions = (items) => {
  if (!items) return [];
  if (!Array.isArray(items)) return [items];
  return items.flatMap((item) =>
    Array.isArray(item) ? flattenLabelOptions(item) : [item]
  );
};

const buildLabelOptions = (...groups) => {
  const labels = new Map();

  groups
    .flatMap(flattenLabelOptions)
    .map(toLabelOption)
    .filter(Boolean)
    .forEach((item) => {
      const key = normalizeLabelValue(item.name);
      const existing = labels.get(key);

      if (!existing || (!existing.colour && item.colour)) {
        labels.set(key, { ...existing, ...item });
      }
    });

  return Array.from(labels.values());
};

const getColorForLabel = (labelName, currentColors = {}) => {
  if (!labelName) return undefined;
  if (currentColors?.[labelName]) return currentColors[labelName];

  const labelKey = normalizeLabelValue(labelName);
  return Object.entries(currentColors || {}).find(
    ([name]) => normalizeLabelValue(name) === labelKey
  )?.[1];
};

const getBestLabelMatch = (labelValue, labelOptions, canUseSuffixMatch) => {
  const normalizedValue = normalizeLabelValue(labelValue);
  if (!normalizedValue) return null;

  const exactMatch = labelOptions.find(
    (item) => normalizeLabelValue(item.name) === normalizedValue
  );
  if (exactMatch) return exactMatch;

  if (!canUseSuffixMatch) return null;

  return labelOptions
    .filter((item) => {
      const labelKey = normalizeLabelValue(item.name);
      return labelKey && normalizedValue.endsWith(labelKey);
    })
    .sort((a, b) => String(b.name).length - String(a.name).length)[0];
};

const isColorValue = (value) =>
  typeof value === "string" && HEX_COLOR.test(value.trim());

const getValidCssColor = (...values) =>
  values.find((value) => {
    if (typeof value !== "string") return false;

    const color = value.trim();
    if (!color || /^-?\d+(\.\d+)?$/.test(color)) return false;

    return (
      isColorValue(color) ||
      /^(rgba?|hsla?)\(/i.test(color) ||
      /^var\(/i.test(color) ||
      /^[a-z]+$/i.test(color)
    );
  });

const toImageDimension = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.round(number) : undefined;
};

const getImageDimension = (detail, dimension) => {
  if (!detail) return undefined;

  const directKeys =
    dimension === "width"
      ? ["img_width", "image_width", "width", "natural_width"]
      : ["img_height", "image_height", "height", "natural_height"];

  const nestedValue =
    dimension === "width"
      ? detail?.image_properties?.width ??
        detail?.imageProperties?.width ??
        detail?.metadata?.width
      : detail?.image_properties?.height ??
        detail?.imageProperties?.height ??
        detail?.metadata?.height;

  return [...directKeys.map((key) => detail?.[key]), nestedValue]
    .map(toImageDimension)
    .find((value) => value !== undefined);
};

const getImageDimensions = (detail) => ({
  width: getImageDimension(detail, "width"),
  height: getImageDimension(detail, "height"),
});

const parseJsonObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return null;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
};

const getAiReasoningSchema = (detail) => {
  const detailSchema = parseJsonObject(
    detail?.response_schema ?? detail?.responseSchema
  );
  if (detailSchema) return detailSchema;

  const reasoningPayload = parseJsonObject(detail?.reasoning);
  if (!reasoningPayload) return null;

  const directSchema =
    reasoningPayload.response_schema ?? reasoningPayload.responseSchema;
  const parsedSchema = parseJsonObject(directSchema);
  if (parsedSchema) return parsedSchema;

  const isDirectReasoningSchema = [
    "confidence_assessment",
    "confidenceAssessment",
    "failure_modes",
    "failureModes",
    "local_suggestion",
    "localSuggestion",
    "prediction_quality",
    "observed_objects",
  ].some((key) => reasoningPayload[key] !== undefined);

  if (isDirectReasoningSchema) return reasoningPayload;

  const nestedReasoning = parseJsonObject(reasoningPayload.reasoning);
  return parseJsonObject(
    nestedReasoning?.response_schema ?? nestedReasoning?.responseSchema
  );
};

const normalizeReasoningList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string") {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const getConfidenceTone = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized.startsWith("high")) return "high";
  if (normalized.startsWith("medium")) return "medium";
  if (normalized.startsWith("low")) return "low";
  return "neutral";
};

const hasAnnotationPayload = (value) => {
  if (Array.isArray(value)) {
    return value.some(hasAnnotationPayload);
  }

  if (value && typeof value === "object") {
    return Object.values(value).some(hasAnnotationPayload);
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (typeof value === "boolean") {
    return value;
  }

  return value !== null && value !== undefined;
};

const hasImageAnnotationPayload = (detail) =>
  hasAnnotationPayload(detail?.annotation) ||
  hasAnnotationPayload(detail?.ground_truth);

const isCoordinateValue = (value) => Number.isFinite(Number(value));

const isGroundTruthBBoxRow = (value) => {
  if (!Array.isArray(value) || value.length < 5) return false;
  return [0, 1, 2, 3].every((index) => isCoordinateValue(value[index]));
};

const isGroundTruthPolygonRow = (value) => {
  if (!Array.isArray(value) || value.length === 0) return false;

  const lastItem = value[value.length - 1];
  return (
    Array.isArray(lastItem) &&
    lastItem.length > 0 &&
    !Array.isArray(lastItem[0]) &&
    !isCoordinateValue(lastItem[0])
  );
};

const getGroundTruthLabelParts = (groundTruth) => {
  if (!Array.isArray(groundTruth)) {
    return { label: groundTruth };
  }

  if (isGroundTruthPolygonRow(groundTruth)) {
    const lastItem = groundTruth[groundTruth.length - 1];
    return { label: lastItem[0], colour: lastItem[1] };
  }

  if (isGroundTruthBBoxRow(groundTruth)) {
    const bboxLabel = groundTruth[4];
    if (Array.isArray(bboxLabel) && bboxLabel.length > 0) {
      return { label: bboxLabel[0], colour: bboxLabel[1] };
    }

    if (
      bboxLabel != null &&
      typeof bboxLabel !== "object" &&
      !isColorValue(bboxLabel)
    ) {
      return { label: bboxLabel, colour: groundTruth[5] };
    }
  }

  let trailingColor;
  for (let index = groundTruth.length - 1; index >= 0; index -= 1) {
    const item = groundTruth[index];

    if (isColorValue(item)) {
      trailingColor = trailingColor || item;
      continue;
    }

    if (typeof item === "string" || typeof item === "number") {
      return { label: item, colour: trailingColor };
    }

    if (item && typeof item === "object" && !Array.isArray(item)) {
      const label = item.name ?? item.label ?? item.class_name ?? item.class;
      if (label) {
        return { label, colour: item.colour ?? item.color ?? trailingColor };
      }
    }
  }

  return { label: null, colour: trailingColor };
};

const hasGroundTruthLabel = (groundTruth) => {
  const { label } = getGroundTruthLabelParts(groundTruth);
  return String(label ?? "").trim().length > 0;
};

const getGroundTruthRows = (groundTruth) => {
  if (!Array.isArray(groundTruth) || groundTruth.length === 0) return [];

  if (hasGroundTruthLabel(groundTruth)) return [groundTruth];

  if (groundTruth.every(hasGroundTruthLabel)) return groundTruth;

  return groundTruth.flatMap((item) => {
    if (hasGroundTruthLabel(item)) return [item];
    if (!Array.isArray(item)) return [];
    return item.filter(hasGroundTruthLabel);
  });
};

const getPredictionLabelParts = (annotation) => {
  if (!Array.isArray(annotation)) {
    return { label: annotation };
  }

  const lastItem = annotation[annotation.length - 1];
  if (
    Array.isArray(lastItem) &&
    lastItem.length > 0 &&
    !Array.isArray(lastItem[0]) &&
    !isCoordinateValue(lastItem[0])
  ) {
    return { label: lastItem[0], colour: lastItem[1] };
  }

  const bboxLabel = annotation[4];
  if (Array.isArray(bboxLabel) && bboxLabel.length > 0) {
    return { label: bboxLabel[0], colour: bboxLabel[1] };
  }

  if (
    bboxLabel != null &&
    typeof bboxLabel !== "object" &&
    !isColorValue(bboxLabel)
  ) {
    return { label: bboxLabel, colour: annotation[5] };
  }

  let trailingColor;
  for (let index = annotation.length - 1; index >= 0; index -= 1) {
    const item = annotation[index];

    if (isColorValue(item)) {
      trailingColor = trailingColor || item;
      continue;
    }

    if (typeof item === "string" || typeof item === "number") {
      return { label: item, colour: trailingColor };
    }
  }

  return { label: null, colour: trailingColor };
};

const unpackGroundTruthLabel = (groundTruth, labelOptions, currentColors) => {
  const { label: rawValue, colour: groundTruthColor } =
    getGroundTruthLabelParts(groundTruth);
  const rawLabel = String(rawValue ?? "").trim();

  if (!rawLabel) return null;

  const hexMatch = rawLabel.match(TRAILING_HEX_COLOR);
  const embeddedColor = hexMatch?.[0];
  const labelWithoutColor = embeddedColor
    ? rawLabel.slice(0, -embeddedColor.length).trim()
    : rawLabel;
  const canUseSuffixMatch = Boolean(embeddedColor) || /^\d/.test(labelWithoutColor);
  const matchedLabel = getBestLabelMatch(
    labelWithoutColor,
    labelOptions,
    canUseSuffixMatch
  );
  const fallbackLabel = canUseSuffixMatch
    ? labelWithoutColor.replace(/^[\d\s:_-]+/, "").trim()
    : labelWithoutColor;
  const name = matchedLabel?.name || fallbackLabel || labelWithoutColor || rawLabel;

  return {
    name,
    colour:
      getColorForLabel(matchedLabel?.name || name, currentColors) ||
      groundTruthColor ||
      embeddedColor ||
      matchedLabel?.colour,
  };
};

const parsePositiveEpochCount = (...values) => {
  for (const value of values) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue > 1) {
      return numericValue;
    }
  }
  return undefined;
};

const normalizeAnnotationEpochGroups = (annotation) => {
  if (!Array.isArray(annotation) || annotation.length === 0) return [];

  const first = annotation[0];
  if (Array.isArray(first)) {
    if (typeof first[0] === "number") {
      return [annotation];
    }
    return annotation;
  }

  return [[annotation]];
};

const getDetailImageSrc = (detail) => {
  const url = detail?.image_url || detail?.image;
  if (!url) return "";
  return url.startsWith("http") || url.startsWith("data:")
    ? url
    : `data:image/jpeg;base64,${url}`;
};

const extractImageNameFromSource = (value, fallbackKey = "") => {
  if (!value) return fallbackKey || null;
  const raw = String(value).trim();
  if (!raw) return fallbackKey || null;

  if (!raw.includes("/") && !raw.startsWith("data:")) {
    return raw;
  }

  const withoutQuery = raw.split("?")[0];
  const fileName = withoutQuery.split("/").pop();
  return fileName || fallbackKey || null;
};

const getExperimentListImageName = (item, key = "") => {
  if (!item) return key || null;

  return (
    item.image_name ||
    extractImageNameFromSource(item.image_url) ||
    extractImageNameFromSource(item.image) ||
    key ||
    null
  );
};

const mergeExperimentPreviewAnnotations = (data, experimentPreviewItems) => {
  if (!experimentPreviewItems?.length) return data;

  const syntheticAnnotations = Object.fromEntries(
    experimentPreviewItems
      .filter((item) => item?.imageName || item?.url || item?.key)
      .map((item) => [
        item.imageName || item.key,
        {
          image_url: item.url,
          image_name: item.imageName || item.key,
        },
      ])
  );

  return {
    ...(data || {}),
    annotations: {
      ...(data?.annotations || {}),
      ...syntheticAnnotations,
    },
  };
};

const formatDisplayValue = (value) => {
  if (value == null || value === "") return "-";
  if (typeof value === "object") return "-";
  return value;
};

const getExperimentFetchErrorMessage = (error) => {
  if (!error || error.name === "AbortError") return null;

  if (typeof error === "string") return error;
  if (error?.message) return error.message;

  const detail = error?.body?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (typeof item === "string" ? item : item?.message || item?.msg))
      .filter(Boolean);
    if (messages.length) return messages.join(", ");
  }

  return "Failed to load prediction details.";
};

const isExperimentBboxRowList = (value) =>
  Array.isArray(value) && value.length > 0 && value.every(isGroundTruthBBoxRow);

const inferExperimentTaskMeta = (detail) => {
  const predictionRows =
    detail?.prediction ?? detail?.predictions ?? detail?.annotation;
  const groundTruthRows = detail?.ground_truth ?? detail?.groundtruth;

  if (isExperimentBboxRowList(predictionRows) || isExperimentBboxRowList(groundTruthRows)) {
    return { task_type: "object_detection", type: "bbox" };
  }

  if (detail?.task_type) {
    return {
      task_type: detail.task_type,
      type: detail.type || detail.task_type,
    };
  }

  return {};
};

const formatExperimentBboxCoordinates = (ann) => {
  if (!Array.isArray(ann) || ann.length < 4) return "-";
  const [x, y, w, h] = ann;
  if (![x, y, w, h].every((value) => Number.isFinite(Number(value)))) return "-";
  return `${Math.round(Number(x))}, ${Math.round(Number(y))}, ${Math.round(Number(w))}, ${Math.round(Number(h))}`;
};

const formatTrackedBboxLabel = (ann, label, showTrackId = false) => {
  const trackId = Array.isArray(ann) && ann.length > 6
    ? ann[ann.length - 1]
    : null;

  return showTrackId && trackId !== null && trackId !== undefined && trackId !== ""
    ? `${label} · ID ${trackId}`
    : label;
};

const ZoomableEnhancementImage = ({ src, alt, onLoad }) => {
  const viewportRef = useRef(null);
  const { transformStyle, viewportHandlers } = useImageZoom({
    viewportRef,
    defaultScale: 1,
  });

  return (
    <div
      ref={viewportRef}
      className="image-enhancement-comparison__viewport"
      {...viewportHandlers}
    >
      <img
        src={src}
        alt={alt}
        className="image-enhancement-comparison__image"
        style={transformStyle}
        draggable="false"
        onLoad={onLoad}
      />
    </div>
  );
};

const normalizeExperimentImageDetail = (detail, imageName = "") => {
  if (!detail || typeof detail !== "object") return detail;

  const taskMeta = inferExperimentTaskMeta(detail);
  const predictionRows =
    detail.prediction ?? detail.predictions ?? null;
  const existingAnnotation = detail.annotation;

  let annotation = existingAnnotation;
  if (!annotation && isExperimentBboxRowList(predictionRows)) {
    annotation = predictionRows;
  } else if (
    !annotation &&
    Array.isArray(predictionRows) &&
    predictionRows.length > 0
  ) {
    annotation = predictionRows;
  }

  return {
    ...detail,
    ...taskMeta,
    image_name: detail.image_name || imageName,
    ground_truth: detail.ground_truth ?? detail.groundtruth ?? detail.groundTruth,
    ...(annotation != null ? { annotation } : {}),
  };
};

let filledForm;
let cleared;
var reloadloop = 0;
var firstkey;
export default function Prediction({
  id,
  type,
  query,
  filter,
  preview,
  visibleCanvas,
  takeCount,
  samUrl,
  modelName,
  tasktype,
  ParentData,
  sliderIndex,
  setSliderIndex,
  setPredictions,
  message,
  videoFrames,
  barData,
  bar_url,
  initialTag = "Train",
  initialPredictionData,
  disableDetailPagination = false,
  experimentTotalEpochs,
  experimentDefaultPredictionEpoch = 0,
  experimentPreviewItems,
  pageSize,
  initialPage,
  initialTotalPages,
  onDetailPageSync,
  loadDetailPage,
}) {
  const resolveExperimentEpoch = useCallback(
    (epochOverride) => {
      if (epochOverride !== undefined && epochOverride !== null && epochOverride !== "") {
        const explicitEpoch = Number(epochOverride);
        if (Number.isFinite(explicitEpoch) && explicitEpoch >= 0) {
          return Math.floor(explicitEpoch);
        }
      }

      const defaultEpoch = Number(experimentDefaultPredictionEpoch);
      return Number.isFinite(defaultEpoch) && defaultEpoch >= 0
        ? Math.floor(defaultEpoch)
        : 0;
    },
    [experimentDefaultPredictionEpoch]
  );

  const initialSlideIndex =
    sliderIndex !== null &&
    sliderIndex !== undefined &&
    sliderIndex !== "" &&
    Number.isInteger(Number(sliderIndex)) &&
    Number(sliderIndex) >= 0
      ? Number(sliderIndex)
      : 0;

  try {
    var queryLength = query?.split("=").length;
  } catch (e) { }
  var modalClass = "";
  const [images, setImages] = React.useState([]);
  const [alert, setAlert] = React.useState();
  const [page, setPage] = React.useState(() => {
    const fromData = Number(initialPredictionData?.active_page);
    if (Number.isFinite(fromData) && fromData >= 1) return fromData;
    const fromProp = Number(initialPage);
    if (Number.isFinite(fromProp) && fromProp >= 1) return fromProp;
    return 1;
  });
  const [annotations, setAnnotations] = React.useState({});
  const [Groundtruth, setGroundtruth] = useState(true);
  const [pridictions, setPridictions] = useState(true);
  const [value, setValue] = useState(tasktype === "Depth Estimation" ? 100 : 50);
  const [epochLength, setEpochLength] = useState(0);
  const [showProperties, setShowProperties] = useState(true);
  const [showLabels, setLabels] = useState(true);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectStatus, setSelectStatus] = useState(false);
  const [tag, setTag] = useState(initialTag || "Train");
  const [imgheight, setImgheight] = useState();
  const [imagewidth, setImgWidth] = useState();
  const [currentCount, setCurrentCount] = useState(0);
  const [form, setFormName] = useState();
  const slideRef = useRef(null);
  const predictionCanvasRef = useRef(null);
  const [currentColors, setCurrentColors] = useState({});
  const task_type = localStorage.getItem("tasktype");
  const [apiresponse, setApiresponse] = useState(false);
  const [hasFetchedInitial, setHasFetchedInitial] = useState(false);
  const [each_image_detail, setEachImagedetail] = useState();
  const [experimentEpoch, setExperimentEpoch] = useState(() =>
    resolveExperimentEpoch(experimentDefaultPredictionEpoch)
  );
  const experimentEpochRef = useRef(resolveExperimentEpoch(experimentDefaultPredictionEpoch));
  const isExperimentImageDetailMode = type === "experiments";
  const [imageCount, setImageCount] = useState(
    takeCount ? 0 : { Train: 0, Test: 0, Val: 0 }
  );
  const [totalPages, setTotalPages] = useState(() => {
    const fromData = Number(initialPredictionData?.page_count);
    if (Number.isFinite(fromData) && fromData >= 1) return fromData;
    const fromProp = Number(initialTotalPages);
    if (Number.isFinite(fromProp) && fromProp >= 1) return fromProp;
    return null;
  });

  useEffect(() => {
    const nextTotalPages = Number(initialTotalPages);
    if (!Number.isFinite(nextTotalPages) || nextTotalPages < 1) return;

    setTotalPages(nextTotalPages);
  }, [initialTotalPages]);
  const [isFetching, setIsFetching] = useState(false);
  const [hiddenLabels, setHiddenLabels] = useState({});
  const [callIndex, setCallIndex] = useState(false);
  const [_id, setId] = useState();
  const [Component, setComponent] = useState();
  const [datasetDeleteUrl, setDatasetDeleteUrl] = useState();
  const [isFirstOpen, setIsFirstOpen] = useState(true);
  const [isTagSwitch, setIsTagSwitch] = useState(false);
  const [newdatalaoding, setnewdataloading] = useState(false);
  const [currentSlideIndex, setcurrentSlideIndex] = useState(initialSlideIndex);
  const [isLabelSelected, setIsLabelSelected] = useState(false);
  const [allLabelsHidden, setAllLabelsHidden] = useState(false);
  const [loader, setloader] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(initialSlideIndex);
  const [showCanvas, setShowCanvas] = useState(
    (task_type === "audio_classification" && false) || visibleCanvas
  );
  const [nextpagefetching, setNextpagefetching] = useState(false);
  const [specView, setSpecView] = useState(false);
  const [spinner, setspinner] = useState(false);
  const [experimentDetailError, setExperimentDetailError] = useState(null);
  const experimentFetchRequestRef = useRef(0);
  const [searchTerm, setSearchTerm] = useState("");
  const originalDataRef = useRef(); // holds full original data
  const [fullImages, setFullImages] = useState("View Images on Bottom");
  const [labelvisible, setlabelsvisible] = useState(true)
  const [currentFrameIndex, setCurrentFrameIndex] = useState(1);
  const [predictionsdataexpand, setpredictionsdataexpand] = useState(true);
  const [moreTooltipOpen, setMoreTooltipOpen] = useState(false);
  const [hoveredAnnotation, setHoveredAnnotation] = useState(null);
  const hoverPrediction = (index, label) =>
    setHoveredAnnotation({ source: "prediction", index, label });
  const hoverGroundTruth = (index, label) =>
    setHoveredAnnotation({
      source: "groundtruth",
      ...(index != null ? { index } : {}),
      ...(label != null ? { label } : {}),
    });
  const [scoreThreshold, setScoreThreshold] = useState(0);
  const [canvasImageSizeByKey, setCanvasImageSizeByKey] = useState({});
  const [zoomControlsPosition, setZoomControlsPosition] = useState({ x: 0, y: 0 });
  const [dicomSliderPosition, setDicomSliderPosition] = useState({ x: 0, y: 0 });
  const [dicomTransitionPreview, setDicomTransitionPreview] = useState("");
  const [storeIndex, setstoreIndex] = useState()
  const [selectedTestIndex, setSelectedTestIndex] = useState(0);
  const indicatorsScrollRef = useRef({ left: 0, top: 0 });
  const pendingRestoreRef = useRef({ on: false, index: 0 });
  const searchResetReadyRef = useRef(false);
  const previousTagRef = useRef(tag);
  const pendingSyncedPageRef = useRef(null);
  const dicomPageCacheRef = useRef(new Map());
  const dicomPageRequestsRef = useRef(new Map());
  const dicomReadyFramesRef = useRef(new Set());
  const pendingDicomFrameRef = useRef(null);

  useEffect(() => {
    dicomPageCacheRef.current.clear();
    dicomPageRequestsRef.current.clear();
    dicomReadyFramesRef.current.clear();
    pendingDicomFrameRef.current = null;
    setDicomTransitionPreview("");
  }, [id, tag]);

  const getIndicatorsEl = () =>
    document.querySelector(".react-slideshow-container + ul.indicators");

  const getSafeSlideIndex = useCallback((value, length, fallback = 0) => {
    if (!length) return 0;

    const numericValue = Number(value);
    if (Number.isInteger(numericValue) && numericValue >= 0) {
      return Math.min(numericValue, length - 1);
    }

    const fallbackValue = Number(fallback);
    if (Number.isInteger(fallbackValue) && fallbackValue >= 0) {
      return Math.min(fallbackValue, length - 1);
    }

    return 0;
  }, []);

  const applySelectedImageDetail = useCallback((detail) => {
    if (!detail) {
      setEachImagedetail();
      setImgheight();
      setImgWidth();
      return;
    }

    const dimensions = getImageDimensions(detail);
    setEachImagedetail(detail);
    setImgheight(dimensions.height);
    setImgWidth(dimensions.width);
  }, []);

  const handleCanvasImageSize = useCallback((imageKey, size) => {
    const width = toImageDimension(size?.width);
    const height = toImageDimension(size?.height);

    if (!imageKey || (!width && !height)) return;

    setCanvasImageSizeByKey((prev) => {
      const previousSize = prev[imageKey] || {};
      if (previousSize.width === width && previousSize.height === height) {
        return prev;
      }

      return {
        ...prev,
        [imageKey]: { width, height },
      };
    });
  }, []);

  const syncSlideLayout = useCallback((index = currentIndex) => {
    if (!ParentData) return false;

    const root = predictionCanvasRef.current;
    const wrapper = root?.querySelector(".react-slideshow-wrapper.slide");
    const inner = wrapper?.querySelector(".images-wrap.horizontal");
    const wrapperWidth = wrapper?.clientWidth || 0;

    if (!inner || !wrapperWidth) return false;

    const slides = Array.from(inner.children);
    inner.style.width = `${wrapperWidth * slides.length}px`;
    // Infinite mode used to prepend a cloned slide, which required the +1
    // offset. Infinite mode is now disabled to avoid duplicate video decoders,
    // so the real slide starts directly at its zero-based index.
    inner.style.transform = `translateX(-${index * wrapperWidth}px)`;

    slides.forEach((slide) => {
      slide.style.width = `${wrapperWidth}px`;
      slide.style.flex = `0 0 ${wrapperWidth}px`;
      slide.style.maxWidth = `${wrapperWidth}px`;
      slide.style.display = "block";
    });

    return true;
  }, [currentIndex, ParentData]);

  const saveIndicatorsScroll = () => {
    const el = getIndicatorsEl();
    if (!el) return;
    indicatorsScrollRef.current = { left: el.scrollLeft, top: el.scrollTop };
  };

  const restoreIndicatorsScroll = () => {
    const el = getIndicatorsEl();
    if (!el) return false;
    const { left, top } = indicatorsScrollRef.current;
    el.scrollLeft = left;
    el.scrollTop = top;
    return true;
  };

  const centerIndicator = (index) => {
    const el = getIndicatorsEl();
    if (!el) return;
    const indicators = el.querySelectorAll(".indicator");
    const active = indicators[index];
    if (!active) return;

    const isLeft = el.classList.contains("left-indicators");
    if (isLeft) {
      const top = active.offsetTop - (el.clientHeight - active.offsetHeight) / 2;
      el.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    } else {
      const left =
        active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2;
      el.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
    }
  };


  useLayoutEffect(() => {
    if (!pendingRestoreRef.current.on) return;

    let attempts = 0;
    const maxAttempts = 10;

    const tryRestore = () => {
      attempts += 1;
      // if indicators aren’t in the DOM yet, try next frame
      if (!restoreIndicatorsScroll()) {
        if (attempts < maxAttempts) requestAnimationFrame(tryRestore);
        return;
      }
      // now center the active one
      centerIndicator(pendingRestoreRef.current.index);
      pendingRestoreRef.current.on = false; // done
    };

    requestAnimationFrame(tryRestore);
  }, [currentIndex]); // currentIndex changes -> Slide remounts -> restore

  const [combinedResult, setCombinedResult] = useState({
    annotations: {},
    form_data: {},
    labels: [],
  });

  const abortControllerRef = useRef(null);


  let cokkie = getCokkie("jwtToken");
  var myHeaders = new Headers();
  myHeaders.append("accept", "application/json");
  myHeaders.append("Authorization", `Bearer ${cokkie}`);

  
  let url = !preview
    ? `${type}/detailed/${id}/predictions`
    : `${type}/data_hub/${id}/predictions`;

  if (samUrl || type === "experiments" || initialPredictionData != null) {
    url = '';
  } else if (bar_url) {
    url = `${bar_url}?filters={"_id":"${id}","plot_name":"${barData?.chartTitle}","plot_value":"${barData.indexValue}"}&section=plot_redirection&page=1&page_size=9999`;
  } else if (query) {
    url += `${query}&page_number=1&tag=${tag}`;
  } else {
    url += `?page_number=1&tag=${tag}`;
  }

  const {
    data: results,
    loading,
    error,
    status,
    setLoad,
    refetch,
  } = useFetch(url, "get");

  const result = ParentData
    ? ParentData
    : initialPredictionData
      ? initialPredictionData
      : results;

  const experimentEpochLength = useMemo(() => {
    if (!isExperimentImageDetailMode) return undefined;

    // Predictions are only available up to the epoch we open with — not the
    // planned Total_epochs from training config (e.g. open at 52 → max 52).
    const availableEpochMax = parsePositiveEpochCount(
      experimentDefaultPredictionEpoch,
      experimentEpoch
    );
    if (availableEpochMax != null) {
      return availableEpochMax;
    }

    return parsePositiveEpochCount(
      each_image_detail?.total_epochs,
      epochLength,
      annotations?.total_epochs,
      initialPredictionData?.total_epochs,
      result?.total_epochs
    );
  }, [
    isExperimentImageDetailMode,
    experimentDefaultPredictionEpoch,
    experimentEpoch,
    each_image_detail?.total_epochs,
    epochLength,
    annotations?.total_epochs,
    initialPredictionData?.total_epochs,
    result?.total_epochs,
  ]);

  useEffect(() => {
    // safe guard
    if (!ParentData?.annotations) return;

    setAnnotations(ParentData);
    originalDataRef.current = ParentData;

    const nextImages = Object.keys(ParentData.annotations)
      .map((k) => {
        const item = ParentData.annotations[k];
        const name =
          task_type !== "audio_classification"
            ? item?.image_name
            : item?.audio_name;

        if (!name) return null;

        return item.media_type === "video" ? k : name;
      })
      .filter(Boolean);

    setImages(prev => {
      const seen = new Set(prev);
      const next = [...prev];

      // iterate in stable key order so order doesn't jump
      for (const name of nextImages) {
        if (name && !seen.has(name)) {
          next.push(name); // append only the newcomers
          seen.add(name);
        }
      }
      return next;
    });

    const activeKey = nextImages[currentIndex] ?? nextImages[0];
    const fallbackKey = Object.keys(ParentData.annotations)[currentIndex] ?? Object.keys(ParentData.annotations)[0];
    const activeDetail = ParentData.annotations[activeKey] ?? ParentData.annotations[fallbackKey];

    applySelectedImageDetail(activeDetail);

    setloader(false);
    setnewdataloading(false);
  }, [ParentData, currentIndex, task_type, applySelectedImageDetail])

  const filteredImages = React.useMemo(() => images.filter((img) => {
    if (!searchTerm.trim()) return true;
    return img.toLowerCase().includes(searchTerm.toLowerCase());
  }), [images, searchTerm]);

  const currentSequenceKey = filteredImages[currentIndex];
  const currentSequenceData = (ParentData ? ParentData : annotations)?.annotations || {};
  const currentSequenceDetail =
    currentSequenceData?.[currentSequenceKey] ||
    Object.values(currentSequenceData)?.[currentIndex] ||
    each_image_detail;
  const currentSequenceSource =
    currentSequenceDetail?.image_url || currentSequenceDetail?.image || "";
  const isDicomSequence = isDicomSource(currentSequenceSource);
  const resolvedDicomPageSize = Number(pageSize) > 0 ? Number(pageSize) : 25;
  const splitCounts = initialPredictionData?.data_split || annotations?.data_split || {};
  const selectedSplitCount =
    tag === "All"
      ? Number(splitCounts.train || 0) + Number(splitCounts.test || 0) + Number(splitCounts.val || 0)
      : Number(splitCounts[String(tag || "").toLowerCase()] || 0);
  const reportedDicomCount = Number(
    initialPredictionData?.img_count ?? annotations?.img_count ?? result?.img_count
  );
  const dicomTotalImages = Math.max(
    1,
    Number.isFinite(reportedDicomCount) && reportedDicomCount > 0
      ? reportedDicomCount
      : selectedSplitCount > 0
        ? selectedSplitCount
        : Math.max(
            filteredImages.length,
            ((Number(totalPages) || 1) - 1) * resolvedDicomPageSize + filteredImages.length
          )
  );
  const currentDicomGlobalIndex = Math.min(
    dicomTotalImages - 1,
    Math.max(0, (Math.max(1, Number(page) || 1) - 1) * resolvedDicomPageSize + currentIndex)
  );

  const captureDicomTransitionFrame = useCallback(() => {
    if (!isDicomSequence) return;
    const canvas = predictionCanvasRef.current?.querySelector(
      ".experiment-detail-canvas-wrap.is-active-dicom-frame .canvas-container canvas.canvas-img"
    );
    if (!canvas?.width || !canvas?.height) return;

    try {
      setDicomTransitionPreview(canvas.toDataURL("image/png"));
    } catch (error) {
      setDicomTransitionPreview("");
    }
  }, [isDicomSequence]);

  const resolveImageName = useCallback((imgKey, imageIndex = null, detailSource = null) => {
    const source = detailSource || (ParentData ? ParentData : annotations);
    const annotationsMap = source?.annotations || {};

    if (imgKey && annotationsMap[imgKey]) {
      const fromItem = getExperimentListImageName(annotationsMap[imgKey], imgKey);
      if (fromItem) return fromItem;
    }

    if (imgKey) {
      const matchedByName = Object.entries(annotationsMap).find(([key, item]) => {
        return (
          key === imgKey ||
          item?.image_name === imgKey ||
          getExperimentListImageName(item, key) === imgKey
        );
      });
      if (matchedByName) {
        return getExperimentListImageName(matchedByName[1], matchedByName[0]);
      }
    }

    if (
      imageIndex !== null &&
      imageIndex !== undefined &&
      Number.isInteger(imageIndex) &&
      imageIndex >= 0
    ) {
      const entries = Object.entries(annotationsMap);
      const byIndex = entries[imageIndex];
      if (byIndex) {
        return getExperimentListImageName(byIndex[1], byIndex[0]);
      }
    }

    return extractImageNameFromSource(imgKey) || imgKey || null;
  }, [ParentData, annotations]);

  const getExperimentImageNameAtIndex = useCallback((imageIndex, imgKey) => {
    if (
      Number.isInteger(imageIndex) &&
      imageIndex >= 0 &&
      experimentPreviewItems?.[imageIndex]?.imageName
    ) {
      return experimentPreviewItems[imageIndex].imageName;
    }

    return resolveImageName(imgKey, imageIndex);
  }, [experimentPreviewItems, resolveImageName]);

  const fetchExperimentImageDetail = useCallback(async (imageName, epoch = 0, clearDetail = false) => {
    if (!isExperimentImageDetailMode || !imageName || !id) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestId = ++experimentFetchRequestRef.current;

    if (clearDetail) {
      applySelectedImageDetail();
    }
    setExperimentDetailError(null);
    setspinner(true);

    try {
      const detail_url = type === 'experiments' ? `${window["ENV"].API_URL}/experiments?action=prediction_image&filter=${encodeURIComponent(JSON.stringify({ exp_id: id }))}&per_page=25&page_number=1&image_name=${encodeURIComponent(imageName)}&epoch=${epoch}` : `${window["ENV"].API_URL}/experiments/${id}/predictions/image?image_name=${encodeURIComponent(imageName)}`;
      const image_details = await getDataFromApi(
        detail_url,
        "get",
        null,
        null,
        controller.signal
      );

      if (requestId !== experimentFetchRequestRef.current) return;

      if (image_details) {
        const availableEpochMax = parsePositiveEpochCount(
          experimentDefaultPredictionEpoch,
          epoch
        );
        const resolvedTotalEpochs =
          availableEpochMax ??
          image_details.total_epochs ??
          parsePositiveEpochCount(epochLength);
        const mergedDetail = normalizeExperimentImageDetail(
          {
            ...image_details,
            ...(resolvedTotalEpochs != null ? { total_epochs: resolvedTotalEpochs } : {}),
          },
          imageName
        );

        applySelectedImageDetail(mergedDetail);
        if (resolvedTotalEpochs != null) {
          setEpochLength(Number(resolvedTotalEpochs));
        }
        setIsTagSwitch(false);
        setCallIndex(true);
      } else {
        const message = `No prediction data returned for epoch ${epoch}.`;
        setExperimentDetailError(message);
        setAlert({ message, status: "error" });
      }
    } catch (error) {
      if (requestId !== experimentFetchRequestRef.current) return;

      const message = getExperimentFetchErrorMessage(error);
      if (message) {
        const contextualMessage =
          epoch > 0
            ? `Failed to load epoch ${epoch}: ${message}`
            : message;
        setExperimentDetailError(contextualMessage);
        setAlert({ message: contextualMessage, status: "error" });
        console.error("Error fetching experiment image details:", error);
      }
    } finally {
      if (requestId === experimentFetchRequestRef.current) {
        setspinner(false);
      }
    }
  }, [id, isExperimentImageDetailMode, applySelectedImageDetail, epochLength, experimentDefaultPredictionEpoch]);

  const loadImageDetailForKey = useCallback((imgKey, epoch, imageIndex = null) => {
    if (isExperimentImageDetailMode) {
      const imageName = getExperimentImageNameAtIndex(imageIndex, imgKey);
      if (!imageName) return;

      const resolvedEpoch = resolveExperimentEpoch(epoch);
      experimentEpochRef.current = resolvedEpoch;
      setExperimentEpoch(resolvedEpoch);
      fetchExperimentImageDetail(imageName, resolvedEpoch);
      return;
    }

    if (!imgKey) {
      applySelectedImageDetail();
      return;
    }

    const detailSource = ParentData ? ParentData : annotations;
    applySelectedImageDetail(detailSource?.annotations?.[imgKey]);
  }, [
    isExperimentImageDetailMode,
    ParentData,
    annotations,
    resolveImageName,
    getExperimentImageNameAtIndex,
    fetchExperimentImageDetail,
    applySelectedImageDetail,
    resolveExperimentEpoch,
  ]);

  const handleExperimentEpochChange = useCallback((epoch) => {
    const annotationEntries = Object.entries(annotations?.annotations || {});
    const imgKey = filteredImages[currentIndex] || annotationEntries[currentIndex]?.[0];
    if (!imgKey && currentIndex < 0) return;

    const imageName = getExperimentImageNameAtIndex(currentIndex, imgKey);
    if (!imageName) return;

    setExperimentEpoch(epoch);
    fetchExperimentImageDetail(imageName, epoch, false);
  }, [
    filteredImages,
    currentIndex,
    annotations,
    getExperimentImageNameAtIndex,
    fetchExperimentImageDetail,
  ]);

  useEffect(() => {
    const defaultEpoch = resolveExperimentEpoch();
    experimentEpochRef.current = defaultEpoch;
    setExperimentEpoch(defaultEpoch);
    setExperimentDetailError(null);
    applySelectedImageDetail();
  }, [id, resolveExperimentEpoch, applySelectedImageDetail]);

  useEffect(() => {
    if (!isExperimentImageDetailMode || !id || !experimentPreviewItems?.length) return;

    const requestedIndex =
      sliderIndex !== null &&
      sliderIndex !== undefined &&
      sliderIndex !== "" &&
      Number.isInteger(Number(sliderIndex)) &&
      Number(sliderIndex) >= 0
        ? Number(sliderIndex)
        : currentIndex;

    const index = getSafeSlideIndex(requestedIndex, experimentPreviewItems.length, 0);
    const imageName = experimentPreviewItems[index]?.imageName;
    if (!imageName) return;

    const defaultEpoch = resolveExperimentEpoch();
    setCurrentIndex(index);
    setcurrentSlideIndex(index);
    experimentEpochRef.current = defaultEpoch;
    setExperimentEpoch(defaultEpoch);
    setShowCanvas(true);
    fetchExperimentImageDetail(imageName, defaultEpoch);
  }, [
    isExperimentImageDetailMode,
    id,
    experimentPreviewItems,
    sliderIndex,
    fetchExperimentImageDetail,
    getSafeSlideIndex,
    resolveExperimentEpoch,
  ]);

  useEffect(() => {
    experimentEpochRef.current = experimentEpoch;
  }, [experimentEpoch]);

  useLayoutEffect(() => {
    if (!ParentData) return;

    let attempts = 0;
    const maxAttempts = 6;

    const sync = () => {
      attempts += 1;
      if (!syncSlideLayout(currentIndex) && attempts < maxAttempts) {
        requestAnimationFrame(sync);
      }
    };

    requestAnimationFrame(sync);
  }, [filteredImages.length, currentIndex, showCanvas, syncSlideLayout, ParentData]);

  useEffect(() => {
  if (sliderIndex === null || sliderIndex === undefined || sliderIndex === "") return;

  const requestedIndex = Number(sliderIndex);
  if (!Number.isInteger(requestedIndex) || requestedIndex < 0) return;

  const annotationEntries = Object.entries(annotations?.annotations || {});
  const listLength = filteredImages.length || annotationEntries.length;
  if (!listLength) return;

  const nextIndex = getSafeSlideIndex(requestedIndex, listLength);
  const imgKey = filteredImages[nextIndex] || annotationEntries[nextIndex]?.[0];

  pendingRestoreRef.current = { on: true, index: nextIndex };

  setCurrentIndex(nextIndex);
  setcurrentSlideIndex(nextIndex);

  if (!isExperimentImageDetailMode) {
    loadImageDetailForKey(imgKey, 0, nextIndex);
  }

  if (fullImages === "View Only Images") {
    setFullImages("View Images on Bottom");
  }

  setShowCanvas(true);

  requestAnimationFrame(() => {
    slideRef.current?.goTo(nextIndex, { skipTransition: true });
    syncSlideLayout(nextIndex);

    requestAnimationFrame(() => {
      scrollIndicatorIntoView(nextIndex);
      centerIndicator(nextIndex);
    });
  });

  setSliderIndex?.(null);
}, [
  sliderIndex,
  filteredImages,
  annotations,
  fullImages,
  isExperimentImageDetailMode,
  setSliderIndex,
  syncSlideLayout,
  getSafeSlideIndex,
  loadImageDetailForKey,
]);
  const thumbCacheRef = useRef(new Map());
  const [thumbMap, setThumbMap] = useState({});
  const thumbnailPageRef = useRef(page);

  useEffect(() => {
    if (
      typeof onDetailPageSync === "function" &&
      thumbnailPageRef.current !== page
    ) {
      thumbCacheRef.current.forEach((url) => {
        if (String(url).startsWith("blob:")) URL.revokeObjectURL(url);
      });
      thumbCacheRef.current.clear();
      setThumbMap({});
      thumbnailPageRef.current = page;
    }
  }, [page, onDetailPageSync]);

  useEffect(() => () => {
    thumbCacheRef.current.forEach((url) => {
      if (String(url).startsWith("blob:")) URL.revokeObjectURL(url);
    });
    thumbCacheRef.current.clear();
  }, []);


  const createSafeThumbFromUrl = async (imageUrl, maxSize = 256) => {
    const img = new Image();
    const renderableSource = await loadRenderableImageSource(imageUrl);
    img.crossOrigin = "anonymous"; // important for canvas

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error("Unable to load image thumbnail"));
      img.src = renderableSource;
    });

    const scale = Math.min(
      maxSize / img.width,
      maxSize / img.height,
      1
    );

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => resolve(URL.createObjectURL(blob)),
        "image/jpeg",
        0.6
      );
    });
  };


  useEffect(() => {
    if (!filteredImages?.length) return;

    let cancelled = false;

    const generateThumbs = async () => {
      for (const imgKey of filteredImages) {
        if (thumbCacheRef.current.has(imgKey)) continue;

        const frameData =
          (ParentData ? ParentData : annotations)?.annotations?.[imgKey];

        if (!frameData) continue;
        if (frameData.media_type === "video") continue;

        const rawUrl =
          frameData.image_url?.startsWith("http")
            ? frameData.image_url
            : frameData.reference_img_url;

        if (!rawUrl) continue;

        let thumbUrl;
        try {
          thumbUrl = await createSafeThumbFromUrl(rawUrl);
        } catch (error) {
          console.error("Unable to create image thumbnail", error);
          continue;
        }

        if (cancelled) return;

        thumbCacheRef.current.set(imgKey, thumbUrl);
        setThumbMap((prev) => ({ ...prev, [imgKey]: thumbUrl }));

        // ⬅️ yield to UI to avoid blocking
        await new Promise((r) => setTimeout(r, 0));
      }
    };

    generateThumbs();

    return () => {
      cancelled = true;
    };
  }, [filteredImages, ParentData, annotations]);

  const shownMediaNames = new Set();

  const properties = {
    duration: 500,
    autoplay: false,
    transitionDuration: 0.1,
    arrows: true,
    infinite: true,
    easing: "ease",
    canSwipe: false,
    indicators: showProperties
      ? (i) => {
        const imgKey = filteredImages[i];
        if (!imgKey) {
          return <div className="indicator d-none" />;
        }

        const frameData = (ParentData ? ParentData : annotations)?.annotations?.[imgKey];

        if (!frameData) {
          return <div className="indicator d-none" />;
        }


        const safeFallback =
          frameData?.image_url?.startsWith("http")
            ? frameData.image_url
            : undefined;


        const mediaType = frameData?.media_type;
        const mediaName = frameData?.media_name;

        if (mediaType === "video") {
          if (shownMediaNames.has(mediaName)) {
            return (
              <div
                style={{ display: "none" }}
                className="indicator hidden-indicator"
              />
            );
          }
          shownMediaNames.add(mediaName);
        }
        const imageUrl = frameData?.image_url || frameData?.reference_img_url;

        const src =
          imageUrl?.startsWith("data:") || imageUrl?.startsWith("https")
            ? imageUrl
            : `data:image/jpeg;base64,${imageUrl}`;

        const checkboxId = `thumb-check-${i}-${imgKey}`;
        const isManualActive = i === currentIndex;
        return (
          <div
            className={`indicator
    ${isManualActive ? "manual-active" : ""}
    ${showCanvas
                ? "full-indicator indicator-close"
                : `half-indicator indicator-show ${task_type === "audio_classification" && "audio-indicator-full"}`
              }
    ${task_type === "audio_classification" && "audio-indicator"}
    ${!spinner ? "custom-hover" : ""}
  `}
          >

            <div className="image-checkbox-label">
              <div className="d-a justify-content-between image-checkbox-label-div" style={{ gap: 2, width: '95%' }}>
                <span
                  className={
                    !moreTooltipOpen &&
                      selectedImages.length === 0 &&
                      !selectedImages.includes(imgKey)
                      ? "custom-visible"
                      : undefined
                  }>
                  {annotations?.annotations?.[imgKey]?.image_name || frameData?.image_name}
                </span>
              </div>

              {/* media OUTSIDE any <label> */}
              {mediaType === "video" &&
              (!isManualActive || !videoFrames?.[mediaName]) ? (
                (() => {
                  const videoData = videoFrames?.[mediaName];
                  return (
                    <EvaluationVideo
                      url={videoData?.url || frameData?.video_stream || frameData?.image_url}
                      frames={videoData?.frames || {}}
                      predictionInterval={videoData?.prediction_interval ?? frameData?.prediction_interval}
                      video_fps={videoData?.video_fps ?? frameData?.video_fps}
                      playMode="hover"
                      labelvisible={true}
                      landmarks={videoData?.landmarksFrames}
                    />
                  );
                })()
              ) : mediaType === "video" ? (
                // The selected video is rendered by the main player below.
                // Do not create a second decoder for the same large source.
                <div className="h-100 w-100" aria-hidden="true" />
              ) : (
                <>
                  <PredictionThumbnail
                    loading="lazy"
                    decoding="async"
                    src={thumbMap[imgKey] ||
                      safeFallback || src ||
                      undefined}
                    onClick={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                      // your click behavior (e.g., open Canvas, load details, etc.)
                      handleThumbClick(imgKey, i); // from previous suggestion
                    }}
                    alt="thumbnail"
                  />

                  {spinner && !isFetching && currentIndex === i && (
                    <div className="thumb-spinner">
                      <FontAwesomeIcon icon={faSpinner} spin />
                    </div>
                  )}
                </>
              )}
            </div>

          </div>
        );
      }
      : undefined,
    onChange: (_oldIndex, newInd) => {
      const newIndex = newInd;
      const syncDetailPages = typeof onDetailPageSync === "function";
      const imgKey = filteredImages[newIndex];

      if (spinner) return;
      saveIndicatorsScroll();

      if (isDicomSequence) {
        if (dicomReadyFramesRef.current.has(imgKey)) {
          pendingDicomFrameRef.current = null;
          setDicomTransitionPreview("");
        } else {
          pendingDicomFrameRef.current = imgKey;
          captureDicomTransitionFrame();
          setspinner(true);
        }
      }

      pendingRestoreRef.current = { on: true, index: newIndex };
      setCurrentIndex(newIndex);
      setcurrentSlideIndex(newIndex);
      setHoveredAnnotation(null);

      if (message !== 'complete') {
        if (!isExperimentImageDetailMode) {
          loadImageDetailForKey(imgKey, 0, newIndex);
        } else {
          const imageName = getExperimentImageNameAtIndex(newIndex, imgKey);
          if (imageName) {
            const epoch = experimentEpochRef.current;
            setExperimentEpoch(epoch);
            fetchExperimentImageDetail(imageName, epoch);
          }
        }
      }

      if (fullImages === "View Only Images") {
        setFullImages("View Images on Bottom");
      }
      setSliderIndex?.(null);

      scrollIndicatorIntoView(newIndex);

      if (
        !syncDetailPages &&
        !disableDetailPagination &&
        newIndex === filteredImages.length - 1 &&
        !isFetching
      ) {
        setIsFetching(true);
        setPage((prevPage) => prevPage + 1);
      }
    },
  };


  const handleThumbClick = async (imgKey, index) => {
    if (spinner) return;
    saveIndicatorsScroll();
    if (isDicomSequence) {
      pendingDicomFrameRef.current = imgKey;
      captureDicomTransitionFrame();
      setspinner(true);
    }
    pendingRestoreRef.current = { on: true, index };

    setCurrentIndex(index);
    setcurrentSlideIndex(index);
    if (message !== 'complete') {
      if (!isExperimentImageDetailMode) {
        loadImageDetailForKey(imgKey, 0, index);
      } else {
        const imageName = getExperimentImageNameAtIndex(index, imgKey);
        if (imageName) {
          const epoch = experimentEpochRef.current;
          setExperimentEpoch(epoch);
          fetchExperimentImageDetail(imageName, epoch);
        }
      }
    }
    if (fullImages === "View Only Images") {
      setFullImages("View Images on Bottom");
    }
    setSliderIndex?.(null);

    slideRef.current?.goTo(index, { skipTransition: true });
    requestAnimationFrame(() => syncSlideLayout(index));
    setShowCanvas(true);
  };



  // --------------- selected image  data and position ------------------- //

  const scrollIndicatorIntoView = (index) => {
    if (nextpagefetching) {
      console.log("making nextpagefetch false");
      setNextpagefetching(false);
    }
    const indicators = document.querySelectorAll(".indicator");
    const activeIndicator = nextpagefetching
      ? indicators[images.length - currentCount]
      : indicators[index];
    if (activeIndicator) {
      activeIndicator.scrollIntoView({
        behavior: "smooth",
        block: nextpagefetching ? "end" : "nearest",
      });
    }
    const imageKeys = annotations?.annotations
      ? Object.values(annotations.annotations)?.[index]
      : undefined;
    const dimensions = getImageDimensions(imageKeys);

    setImgheight(dimensions.height);
    setImgWidth(dimensions.width);
    setSliderIndex?.(null);
    setIsFirstOpen(false);
  };

  const imageData = annotations?.annotations

  const topLabels =
    annotations?.labels?.length > 0
      ? annotations.labels
      : imageData && Object.entries(imageData)?.[0]?.[1]?.labels;

  function getIndexByAnnotationKey(targetKey) {
    const keys = annotations?.annotations
      ? Object.keys(annotations.annotations)
      : [];
    console.log(keys.indexOf(targetKey));
    return keys.indexOf(targetKey);
  }

  const handleImageDetail = async (image_name, epoch = experimentEpoch) => {
    if (ParentData) return;
    if (!image_name) {
      console.warn("Image name is not provided. Skipping API call.");
      return;
    }

    if (type === "experiments") {
      await fetchExperimentImageDetail(image_name, epoch);
      return;
    }

    console.log('image_name', image_name)
    applySelectedImageDetail();

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    function getIdFromQuery(queryString) {
      const params = new URLSearchParams(queryString);
      return params.get("evaluation_id");
    }

    const ele_id = getIdFromQuery(query);
    const detail_url = query
      ? `${window["ENV"].API_URL}/models/img_detailed/${ele_id}/${image_name}?tag=${tag}&visual_inspection=false`
      : samUrl
          ? `${window["ENV"].API_URL}/evaluations?filters={"_id":"${id}","image_name":"${image_name}"}&section=predictions`
          : bar_url
            ? `${window["ENV"].API_URL}/evaluations?filters={"_id":"${id}","image_name":"${image_name}"}&section=predictions`
            : `${window["ENV"].API_URL}/dataset_versions/detailed/${id}/${image_name}?tag=${tag}`;
    try {
      const image_details = await getDataFromApi(
        detail_url,
        "get",
        null,
        null,
        controller.signal
      );
      if (image_details) {
        applySelectedImageDetail(image_details);
        setIsTagSwitch(false);
        setCallIndex(true);
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Previous request was canceled.");
      } else {
        console.error("Error fetching image details:", error);
      }
    }
  };

  useEffect(() => {
    if (ParentData) return;

    if (result && !hasFetchedInitial) {
      let i = 1
      console.log('how may times it is calling ', i++)
      const { Train, Test, Val } = result?.data_split || {};
      if (takeCount) {
        setImageCount(result.img_count);
      } else {
        setImageCount({ Train, Test, Val });
      }

      setTotalPages(result?.page_count);
      setCurrentCount(result?.current_count);
      
      const initialCombined = mergeResults({}, result);
      setCombinedResult(initialCombined);
      
      originalDataRef.current = initialCombined;
      unzipAndSelect(initialCombined);
      

      setHasFetchedInitial(true);
      
    }
  }, [result, hasFetchedInitial, ParentData, isExperimentImageDetailMode, experimentPreviewItems]);

  useEffect(() => {
    if (ParentData || isExperimentImageDetailMode) return;

    if (previousTagRef.current === tag) return;

    previousTagRef.current = tag;
    setloader(true);
    setHasFetchedInitial(false);
    setPage(1);
    setImages([]);
    setCurrentIndex(0);
    setcurrentSlideIndex(0);
    applySelectedImageDetail();
    setSliderIndex?.(null);
  }, [tag, ParentData, setSliderIndex, applySelectedImageDetail]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollHeight, scrollTop, clientHeight } = e.target;
      if (typeof onDetailPageSync === "function") return;

      if (!disableDetailPagination && scrollHeight - scrollTop <= clientHeight * 1.1 && !newdatalaoding) {
        if (page < totalPages) {
          setnewdataloading(true);
          setIsFetching(true);
          setPage(page + 1);
        }
      }
    },
    [disableDetailPagination, newdatalaoding, onDetailPageSync, page, totalPages]
  );

  useEffect(() => {
    const indicatorsContainer = document.querySelector(
      ".react-slideshow-container + ul.indicators"
    );

    if (!indicatorsContainer) return;
    
    if (fullImages === "") {
      indicatorsContainer.classList.add("no-indicators");
      indicatorsContainer.classList.remove(
        "full-indicators",
        "left-indicators",
        "bottom-indicators"
      );
    } else if (fullImages === "View Only Images") {
      indicatorsContainer.classList.add("full-indicators");
      indicatorsContainer.classList.remove(
        "no-indicators",
        "left-indicators",
        "bottom-indicators"
      );
    } else if (fullImages === "View Images on left") {
      indicatorsContainer.classList.add("left-indicators");
      indicatorsContainer.classList.remove(
        "full-indicators",
        "no-indicators",
        "bottom-indicators"
      );
    } else if (fullImages === "View Images on Bottom") {
      indicatorsContainer.classList.add("bottom-indicators");
      indicatorsContainer.classList.remove(
        "full-indicators",
        "no-indicators",
        "left-indicators"
      );
    } else {
      indicatorsContainer.classList.add("no-indicators");
      indicatorsContainer.classList.remove(
        "full-indicators",
        "left-indicators",
        "bottom-indicators"
      );
    }
    
  }, [fullImages, showCanvas, isFetching, page, annotations, currentIndex]);

  useEffect(() => {
    if (typeof onDetailPageSync === "function") return;

    const container = document.querySelector(
      ".react-slideshow-container+ul.indicators"
    );
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll, onDetailPageSync]);

  useEffect(() => {
    if (typeof onDetailPageSync === "function") {
      return;
    }

    if (!disableDetailPagination && page > 1) {
      fetchNextPage();
    }
  }, [page, disableDetailPagination, onDetailPageSync]);

  const buildPredictionsPageUrl = (pageNumber) => {
    const resolvedPageSize =
      Number(pageSize) > 0
        ? Number(pageSize)
        : type === "experiments"
          ? 30
          : 200;
    const sizeQuery = `&page_number=${pageNumber}&page_size=${resolvedPageSize}&per_page=${resolvedPageSize}`;

    if (type === "experiments") {
      return `${window["ENV"].API_URL}/experiments?action=predictions&filter=${encodeURIComponent(
        JSON.stringify({ exp_id: id })
      )}${sizeQuery}`;
    }

    if (query) {
      return `${window["ENV"].API_URL}/${type}/detailed/${id}/predictions${query}&tag=${encodeURIComponent(tag)}${sizeQuery}`;
    }

    return `${window["ENV"].API_URL}/${type}/detailed/${id}/predictions?tag=${encodeURIComponent(tag)}${sizeQuery}`;
  };

  const loadDicomPage = async (targetPage) => {
    if (dicomPageCacheRef.current.has(targetPage)) {
      return dicomPageCacheRef.current.get(targetPage);
    }
    if (dicomPageRequestsRef.current.has(targetPage)) {
      return dicomPageRequestsRef.current.get(targetPage);
    }

    const request = (typeof loadDetailPage === "function"
      ? loadDetailPage(targetPage)
      : getDataFromApi(buildPredictionsPageUrl(targetPage), "get"))
      .then((pageResult) => {
        if (pageResult && !pageResult.detail) {
          dicomPageCacheRef.current.set(targetPage, pageResult);
          while (dicomPageCacheRef.current.size > 3) {
            const oldestPage = dicomPageCacheRef.current.keys().next().value;
            dicomPageCacheRef.current.delete(oldestPage);
          }
        }
        return pageResult;
      })
      .finally(() => {
        dicomPageRequestsRef.current.delete(targetPage);
      });

    dicomPageRequestsRef.current.set(targetPage, request);
    return request;
  };

  const fetchSyncedDetailPage = async (targetPage, focus = "start") => {
    if (disableDetailPagination) return;
    if (isFetching) return;

    const maxPage = Number(totalPages);
    if (targetPage < 1) return;
    if (Number.isFinite(maxPage) && targetPage > maxPage) return;

    setIsFetching(true);

    let pageApplied = false;
    try {
      setspinner(true);
      const pageResult = isDicomSequence
        ? await loadDicomPage(targetPage)
        : typeof loadDetailPage === "function"
          ? await loadDetailPage(targetPage)
          : await getDataFromApi(buildPredictionsPageUrl(targetPage), "get");

      if (!pageResult || pageResult.detail) {
        return;
      }

      const nextPage = Number(pageResult.active_page) || targetPage;
      if (pageResult.page_count) {
        setTotalPages(Number(pageResult.page_count));
      }

      setCombinedResult(pageResult);
      originalDataRef.current = pageResult;

      const keys = Object.keys(pageResult.annotations || {});
      const requestedIndex = Number(focus);
      const nextIndex = Number.isInteger(requestedIndex)
        ? Math.max(0, Math.min(requestedIndex, Math.max(0, keys.length - 1)))
        : focus === "end"
          ? Math.max(0, keys.length - 1)
          : 0;
      const nextKey = keys[nextIndex];

      await unzipAndSelect(pageResult, {
        replaceImages: true,
        preferredIndex: nextIndex,
      });

      pendingRestoreRef.current = { on: true, index: nextIndex };
      setCurrentIndex(nextIndex);
      setcurrentSlideIndex(nextIndex);
      setHoveredAnnotation(null);
      setPage(nextPage);
      pageApplied = true;

      if (nextKey && message !== "complete") {
        if (isExperimentImageDetailMode) {
          const pageEntries = Object.entries(pageResult.annotations || {});
          const [entryKey, entryValue] = pageEntries[nextIndex] || pageEntries[0] || [];
          const imageName =
            entryValue?.image_name ||
            getExperimentListImageName(entryValue, entryKey) ||
            nextKey;
          if (imageName) {
            fetchExperimentImageDetail(
              imageName,
              experimentEpochRef.current
            );
          }
        } else {
          loadImageDetailForKey(nextKey, 0, nextIndex);
        }
      }

      requestAnimationFrame(() => {
        slideRef.current?.goTo(nextIndex, { skipTransition: true });
        syncSlideLayout(nextIndex);
      });

      onDetailPageSync?.({
        page: nextPage,
        data: pageResult,
        index: nextIndex,
      });
    } catch (error) {
      console.error("Error fetching prediction detail page:", error);
      if (isDicomSequence) {
        setDicomTransitionPreview("");
      }
    } finally {
      setIsFetching(false);
      setnewdataloading(false);
      if (!isDicomSequence || !pageApplied) {
        setspinner(false);
      }
    }
  };

  const navigateToDicomSlice = async (oneBasedIndex) => {
    if (!isDicomSequence || isFetching) return;

    const targetGlobalIndex = Math.max(
      0,
      Math.min(dicomTotalImages - 1, Number(oneBasedIndex) - 1)
    );
    const targetPage = Math.floor(targetGlobalIndex / resolvedDicomPageSize) + 1;
    const targetLocalIndex = targetGlobalIndex % resolvedDicomPageSize;

    if (targetPage === page) {
      const safeIndex = Math.min(targetLocalIndex, Math.max(0, filteredImages.length - 1));
      const targetKey = filteredImages[safeIndex];
      if (targetKey && safeIndex !== currentIndex) {
        await handleThumbClick(targetKey, safeIndex);
      }
      return;
    }

    captureDicomTransitionFrame();
    await fetchSyncedDetailPage(targetPage, targetLocalIndex);
  };

  useEffect(() => {
    if (!isDicomSequence) return;

    const currentPageData = initialPredictionData?.active_page === page
      ? initialPredictionData
      : annotations;
    if (currentPageData?.annotations) {
      dicomPageCacheRef.current.set(page, currentPageData);
    }

    const nearbySources = [];
    for (
      let index = Math.max(0, currentIndex - 2);
      index <= Math.min(filteredImages.length - 1, currentIndex + 3);
      index += 1
    ) {
      const key = filteredImages[index];
      const detail = currentSequenceData?.[key] || Object.values(currentSequenceData)?.[index];
      const source = detail?.image_url || detail?.image;
      if (isDicomSource(source)) nearbySources.push(source);
    }
    nearbySources.forEach((source) => {
      loadRenderableImageSource(source).catch(() => {});
    });

    if (
      filteredImages.length - currentIndex <= 5 &&
      page < Number(totalPages || 1)
    ) {
      loadDicomPage(page + 1)
        .then((nextPageData) => {
          const nextSources = Object.values(nextPageData?.annotations || {})
            .slice(0, 4)
            .map((detail) => detail?.image_url || detail?.image)
            .filter(isDicomSource);
          nextSources.forEach((source) => {
            loadRenderableImageSource(source).catch(() => {});
          });
        })
        .catch(() => {});
    }

    if (currentIndex < 5 && page > 1) {
      loadDicomPage(page - 1)
        .then((previousPageData) => {
          const previousDetails = Object.values(
            previousPageData?.annotations || {}
          );
          const previousSources = previousDetails
            .slice(Math.max(0, previousDetails.length - 4))
            .map((detail) => detail?.image_url || detail?.image)
            .filter(isDicomSource);

          previousSources.forEach((source) => {
            loadRenderableImageSource(source).catch(() => {});
          });
        })
        .catch(() => {});
    }
  }, [
    isDicomSequence,
    currentIndex,
    page,
    totalPages,
    filteredImages,
    currentSequenceData,
    initialPredictionData,
  ]);

  const canGoSyncedPrevPage =
    typeof onDetailPageSync === "function" && page > 1 && !isFetching;
  const canGoSyncedNextPage =
    typeof onDetailPageSync === "function" &&
    !isFetching &&
    (totalPages ? page < totalPages : filteredImages.length >= (Number(pageSize) || 30));

  const fetchNextPage = async () => {
    if (!disableDetailPagination && page > 1 && page <= totalPages) {
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const nextPageResult = await getDataFromApi(
          buildPredictionsPageUrl(page),
          "get"
        );
        
        const updatedCombinedResult = mergeResults(
          combinedResult,
          nextPageResult
        );
        setCombinedResult(updatedCombinedResult);
        originalDataRef.current = updatedCombinedResult;
        unzipAndSelect(updatedCombinedResult);
        console.log("making nextpagefetch true");
        setNextpagefetching(true);
        
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsFetching(false);
      }
    }
  };

  useEffect(() => {
    if (slideRef.current && images.length) {
      const targetIndex = Math.min(currentSlideIndex, images.length - 1);
      slideRef.current.goTo(targetIndex, { skipTransition: true });
      requestAnimationFrame(() => syncSlideLayout(targetIndex));
    }
  }, [images, currentSlideIndex, syncSlideLayout]);

  async function unzipAndSelect(incomingData, { replaceImages = false, preferredIndex = null } = {}) {
    const data =
      isExperimentImageDetailMode && !replaceImages
        ? mergeExperimentPreviewAnnotations(incomingData, experimentPreviewItems)
        : incomingData;

    setLoad(true);
    filledForm = data?.form_data;

    if (filledForm && filledForm.Filters) {
      cleared = Object.keys(filledForm.Filters).length;
    } else {
      cleared = filledForm ? Object.keys(filledForm).length : 0;
    }
    
    setAnnotations(data);

    let firstkey = null;
    let newImages = [];
    if (data && data.annotations) {
      const annotationKeys = Object.keys(data.annotations);
      if (annotationKeys.length > 0) {
        firstkey = annotationKeys[0];
      }

      if (
        isExperimentImageDetailMode &&
        experimentPreviewItems?.length &&
        !replaceImages
      ) {
        newImages = experimentPreviewItems
          .map((item) => item.imageName)
          .filter(Boolean);
      } else {
        newImages = Object.entries(data.annotations).map(([key, item]) => {
          const name =
            task_type !== "audio_classification"
              ? getExperimentListImageName(item, key)
              : item?.audio_name || key;

          if (!name) return null;

          return item.media_type === "video" && item.media_name
            ? `${item.media_name}-${name}`
            : name;
        }).filter(Boolean);
      }

      const firstAnnotation = data.annotations[firstkey];
      if (isExperimentImageDetailMode) {
        const availableEpochMax = parsePositiveEpochCount(
          experimentDefaultPredictionEpoch
        );
        const listEpochTotal =
          availableEpochMax ??
          parsePositiveEpochCount(
            data?.total_epochs,
            firstAnnotation?.total_epochs,
            firstAnnotation?.annotation?.length
          );
        if (listEpochTotal) {
          setEpochLength(listEpochTotal);
        } else {
          setEpochLength(0);
        }
      } else if (firstAnnotation && firstAnnotation?.annotation) {
        setEpochLength(firstAnnotation?.annotation.length);
      } else {
        setEpochLength(0);
      }
    } else if (isExperimentImageDetailMode && experimentPreviewItems?.length && !replaceImages) {
      newImages = experimentPreviewItems
        .map((item) => item.imageName)
        .filter(Boolean);

      const listEpochTotal =
        parsePositiveEpochCount(experimentDefaultPredictionEpoch) ??
        parsePositiveEpochCount(data?.total_epochs);
      setEpochLength(listEpochTotal || 0);
    } else {
      newImages = [];
      setEpochLength(0);
    }
    
    setImages((prev) => {
      if (isExperimentImageDetailMode || replaceImages) {
        return newImages;
      }

      const seen = new Set(prev);
      const merged = [...prev];
      for (const name of newImages) {
        if (name && !seen.has(name)) {
          merged.push(name);
          seen.add(name);
        }
      }
      return merged;
    });

    if (newImages.length > 0) {
      const targetIndex = getSafeSlideIndex(
        preferredIndex ?? sliderIndex ?? currentIndex,
        newImages.length,
        preferredIndex ?? 0
      );
      const previewItem = experimentPreviewItems?.[targetIndex];
      const selectedImageKey =
        previewItem?.key ||
        previewItem?.imageName ||
        newImages[targetIndex] ||
        Object.keys(data?.annotations || {})[targetIndex] ||
        firstkey;
      const selectedDetail =
        data?.annotations?.[selectedImageKey] ??
        Object.values(data?.annotations || {})?.[targetIndex] ??
        data?.annotations?.[firstkey];

      setCurrentIndex(targetIndex);
      setcurrentSlideIndex(targetIndex);
      if (isExperimentImageDetailMode) {
        const defaultEpoch = resolveExperimentEpoch();
        experimentEpochRef.current = defaultEpoch;
        setExperimentEpoch(defaultEpoch);
      } else {
        applySelectedImageDetail(selectedDetail);
      }
    } else {
      setCurrentIndex(0);
      setcurrentSlideIndex(0);
      if (isExperimentImageDetailMode) {
        const defaultEpoch = resolveExperimentEpoch();
        experimentEpochRef.current = defaultEpoch;
        setExperimentEpoch(defaultEpoch);
      }
      applySelectedImageDetail();
    }

    setApiresponse(false);
    reloadloop = 0;
    setLoad(false);
    setloader(false);
    setnewdataloading(false);
  }


  const mergeResults = (prevResult, newResult) => {
    const mergedAnnotations = {
      ...prevResult.annotations,
      ...(newResult.annotations || {}),
    };

    const mergedFormData = {
      ...prevResult.form_data,
      ...(newResult.form_data || {}),
    };

    const prevLabels = Array.isArray(prevResult.labels)
      ? prevResult.labels
      : [];
    const newLabels = Array.isArray(newResult.labels) ? newResult.labels : [];

    const uniqueLabelsSet = new Set(
      prevLabels.map((label) => JSON.stringify(label))
    );

    newLabels.forEach((label) => {
      uniqueLabelsSet.add(JSON.stringify(label));
    });

    return {
      annotations: mergedAnnotations,
      form_data: mergedFormData,
      labels: Array.from(uniqueLabelsSet).map((label) => JSON.parse(label)),
    };
  };
  
  const clearsearch = () => {
    if (storeIndex !== undefined) {
      
      setCurrentIndex(storeIndex);

      
      setTimeout(() => {
        setSearchTerm('');
        setNoMatch(false);

        if (originalDataRef.current) {
          unzipAndSelect(originalDataRef.current);
        }
      }, 0);
    } else {
      setSearchTerm('');
      setNoMatch(false);
      if (originalDataRef.current) {
        unzipAndSelect(originalDataRef.current);
      }
    }
  }
  // ------------------ Label color -----------------------//

  useEffect(() => {
    if (topLabels?.length) {
      setCurrentColors((prevColors) => {
        const updatedColors = topLabels?.reduce((acc, label) => {
          if (!label?.name) return acc;
          acc[label.name] = prevColors?.[label.name] || label.colour || label.color;
          return acc;
        }, {});
        return updatedColors;
      });
    }
  }, [topLabels]);

  const handleColorChange = (labelName, newColor) => {
    setCurrentColors((prevColors) => ({
      ...prevColors,
      [labelName]: newColor,
    }));
  };




  // ------------------Filter through Search input-------------//

  const [noMatch, setNoMatch] = useState(false); 

  useEffect(() => {
    if (!searchResetReadyRef.current) {
      searchResetReadyRef.current = true;
      return;
    }

    if (searchTerm === '') {
      setCurrentIndex(0);
      setcurrentSlideIndex(0);
    }
  }, [searchTerm])

  const handleSearch = (e) => {
    const inputValue = e.target.value;
    const term = inputValue.toLowerCase();
    setSearchTerm(term);

    const fullData = originalDataRef.current;
    if (!fullData || !fullData.annotations) return;

    if (term.trim() === "") {
      unzipAndSelect(fullData);
      return;
    }

    // 🧩 filter results
    const filteredAnnotations = Object.fromEntries(
      Object.entries(fullData.annotations).filter(([_, value]) => {
        const name =
          task_type !== "audio_classification"
            ? value.image_name
            : value.audio_name;
        return name && name.toLowerCase().includes(term);
      })
    );

    // ❌ no match found
    if (Object.keys(filteredAnnotations).length === 0) {
      setNoMatch(true);

      // 🔥 trigger re-render of Slide
      setstoreIndex(currentIndex);
      setCurrentIndex(currentIndex === 0 ? 1 : 0);
    } else {
      setNoMatch(false);
      unzipAndSelect({
        ...fullData,
        annotations: filteredAnnotations,
      });
    }
  };

  const hasAnyImageAnnotations = React.useMemo(() => {
    const annotationSource = (ParentData ? ParentData : annotations)?.annotations;
    const details = annotationSource ? Object.values(annotationSource) : [];

    return [...details, each_image_detail].some(hasImageAnnotationPayload);
  }, [ParentData, annotations, each_image_detail]);

  const canUseLabelVisibilityControls = hasAnyImageAnnotations;

  useEffect(() => {
    if (canUseLabelVisibilityControls) return;

    setAllLabelsHidden(false);
    setHiddenLabels({});
    setIsLabelSelected(false);
  }, [canUseLabelVisibilityControls]);

  // ------------------------ groundtruth and predictions hide functionality ------------------------------ //
  const getClassFilterKey = (labelName) => normalizeLabelValue(labelName);

  const isClassVisible = (labelName) => {
    if (allLabelsHidden) return false;

    const labelKey = getClassFilterKey(labelName);
    if (!labelKey) return !isLabelSelected;

    if (isLabelSelected) {
      return Boolean(hiddenLabels[labelKey]);
    }

    return !hiddenLabels[labelKey];
  };

  const toggleLabelVisibility = (labelName) => {
    if (!canUseLabelVisibilityControls) return;

    const labelKey = getClassFilterKey(labelName);
    if (!labelKey) return;

    setAllLabelsHidden(false);

    if (isLabelSelected && hiddenLabels[labelKey]) {
      setHiddenLabels({});
      setIsLabelSelected(false);
      return;
    }

    setHiddenLabels({ [labelKey]: true });
    setIsLabelSelected(true);
  };

  const toggleAllLabelVisibility = () => {
    if (!canUseLabelVisibilityControls) return;

    setAllLabelsHidden((prev) => !prev);
    setHiddenLabels({});
    setIsLabelSelected(false);
  };

  const handleChange = () => {
    setGroundtruth(!Groundtruth);
  };

  const groundTruthLabelOptions = buildLabelOptions(
    annotations?.labels,
    topLabels,
    each_image_detail?.labels,
    each_image_detail?.label
  );

  const getGroundTruthFilterLabel = (groundTruthItem) => {
    const unpackedLabel = unpackGroundTruthLabel(
      groundTruthItem,
      groundTruthLabelOptions,
      currentColors
    )?.name;

    return unpackedLabel || getGroundTruthLabelParts(groundTruthItem).label;
  };

  const filterGroundTruthByClassVisibility = (groundTruth) => {
    if (groundTruth == null) return groundTruth;

    if (!Array.isArray(groundTruth)) {
      return isClassVisible(groundTruth) ? groundTruth : null;
    }

    if (hasGroundTruthLabel(groundTruth)) {
      return isClassVisible(getGroundTruthFilterLabel(groundTruth))
        ? groundTruth
        : null;
    }

    return groundTruth
      .map((item) => filterGroundTruthByClassVisibility(item))
      .filter((item) => (Array.isArray(item) ? item.length > 0 : item != null));
  };


  const sourceGroundtruth = each_image_detail?.ground_truth
    ? each_image_detail.ground_truth
    : annotations?.annotations?.[
      Object?.keys(annotations?.annotations)?.[0]
    ]?.ground_truth;

  const filteredGroundtruth = filterGroundTruthByClassVisibility(sourceGroundtruth);

  const detailType = each_image_detail?.type;
  const detailTaskType = String(
    each_image_detail?.task_type || tasktype || ""
  ).toLowerCase().replace(/[\s-]+/g, "_");
  const isSuperpointKeypointDetection =
    normalizeLabelValue(
      modelName || each_image_detail?.model_name || each_image_detail?.model
    ) === "superpoint" && detailTaskType === "keypoint_detection";
  const superpointLandmarkPoints = (() => {
    if (!isSuperpointKeypointDetection) return [];

    const collectPoints = (value) => {
      if (!Array.isArray(value)) return [];
      if (
        value.length >= 3 &&
        [value[0], value[1], value[2]].every((item) =>
          Number.isFinite(Number(item))
        )
      ) {
        return [value];
      }

      return value.flatMap(collectPoints);
    };

    return collectPoints(each_image_detail?.landmarks);
  })();
  const detailAnnotation = each_image_detail?.annotation;
  const firstAnnotationGroup = (() => {
    if (!Array.isArray(detailAnnotation) || detailAnnotation.length === 0) return [];

    if (isGroundTruthBBoxRow(detailAnnotation[0])) {
      return detailAnnotation;
    }

    if (Array.isArray(detailAnnotation[0])) {
      return detailAnnotation[0];
    }

    return [];
  })();

  const isShapeGroundTruth =
    detailTaskType === "object_detection" ||
    each_image_detail?.type === "bbox" ||
    each_image_detail?.type === "polygon" ||
    (isExperimentImageDetailMode &&
      isExperimentBboxRowList(each_image_detail?.ground_truth));

  const objectDetectionGroundTruthRows =
    isShapeGroundTruth &&
      Array.isArray(each_image_detail?.ground_truth)
      ? getGroundTruthRows(each_image_detail.ground_truth)
      : [];

  const rawClassificationGroundTruth = Array.isArray(each_image_detail?.ground_truth)
    ? each_image_detail.ground_truth
    : each_image_detail?.ground_truth
      ? [each_image_detail.ground_truth]
      : [];

  const classificationGroundTruthItems =
    each_image_detail?.type === "classification"
      ? rawClassificationGroundTruth
        .map((item) =>
          unpackGroundTruthLabel(item, groundTruthLabelOptions, currentColors)
        )
        .filter(Boolean)
      : [];

  const groundTruthLabelItems = (() => {
    if (!isShapeGroundTruth) return [];

    if (objectDetectionGroundTruthRows.length > 0) {
      const uniqueLabels = new Map();

      objectDetectionGroundTruthRows.forEach((ann) => {
        const labelInfo = unpackGroundTruthLabel(
          ann,
          groundTruthLabelOptions,
          currentColors
        );
        const labelName = labelInfo?.name;
        if (!labelName || uniqueLabels.has(labelName)) return;

        uniqueLabels.set(labelName, {
          name: labelName,
          colour: labelInfo?.colour,
        });
      });

      return Array.from(uniqueLabels.values());
    }

    return [];
  })();

  const keypointGroundTruthLabelItems = (() => {
    if (each_image_detail?.task_type !== "pose_estimation") return [];

    if (Array.isArray(each_image_detail?.gt_labels) && each_image_detail.gt_labels.length > 0) {
      return each_image_detail.gt_labels
        .map((item, idx) => {
          const labelData = Array.isArray(item) ? item[0] : item;
          const rawPointNumber = Array.isArray(item)
            ? item[1]
            : labelData?.number ?? labelData?.point_number ?? idx + 1;
          const pointNumber = rawPointNumber ?? idx + 1;
          const labelName =
            typeof labelData === "string"
              ? labelData
              : labelData?.name ?? labelData?.label ?? labelData?.class_name ?? labelData?.class;

          if (!labelName) return null;

          return {
            name: labelName,
            number: pointNumber,
            colour: labelData?.colour ?? labelData?.color ?? currentColors?.[labelName],
          };
        })
        .filter(Boolean);
    }

    return Array.isArray(each_image_detail?.label?.[0])
      ? each_image_detail.label[0].map((labelName, idx) => ({
        name: labelName,
        number: idx + 1,
        colour: currentColors?.[labelName],
      }))
      : [];
  })();

  const classFilterLabels = buildLabelOptions(
    topLabels,
    groundTruthLabelItems,
    classificationGroundTruthItems,
    keypointGroundTruthLabelItems
  );

  const selectedLabelKey = isLabelSelected
    ? Object.keys(hiddenLabels).find((key) => hiddenLabels[key]) || null
    : null;


  const getAnnotationScore = (annotation, idx, detail) => {
    if (!annotation || !detail) return 0;

    // classification
    if (detail?.type === "classification") {
      const score = annotation?.[5];
      return typeof score === "number" ? score : Number(score) || 0;
    }

    // segmentation: score usually lives in ann[2]
    if (detail?.task_type === "segmentation") {
      return Number(annotation?.[2]) || 0;
    }

    // object detection / Pose Estimation / depth estimation
    if (Array.isArray(detail?.score)) {
      return Number(detail.score?.[idx]) || 0;
    }

    // fallback guesses
    if (typeof annotation?.[5] === "number") return annotation[5];
    if (typeof annotation?.[5]?.score === "number") return annotation[5].score;

    return 0;
  };

  const getRoundedScore = (value) => Number(Number(value || 0).toFixed(2));
  const predictionLabelOptions = buildLabelOptions(
    topLabels,
    each_image_detail?.labels
  );

    const isDepthEstimationModel = (detail) =>
    String(detail?.task_type || tasktype || "")
      .toLowerCase()
      .replace(/[\s-]+/g, "_") === "depth_estimation";

  const getPredictionColorForRow = (annotation, idx, fallbackLabel) => {
    const { label, colour } = getPredictionLabelParts(annotation);
    const labelName = fallbackLabel ?? label;
    const matchedLabel = getBestLabelMatch(
      labelName,
      predictionLabelOptions,
      true
    );
    const indexedLabelColor = Array.isArray(each_image_detail?.labels)
      ? (each_image_detail.labels?.[idx]?.colour ?? each_image_detail.labels?.[idx]?.color)
      : undefined;

    if (isDepthEstimationModel(each_image_detail)) {
      return getValidCssColor(
        indexedLabelColor,
        colour,
        matchedLabel?.colour,
        getColorForLabel(matchedLabel?.name || labelName, currentColors) ||
          getColorForLabel(labelName, currentColors),
        "#6b7280"
      );
    }

    return getValidCssColor(
      getColorForLabel(matchedLabel?.name || labelName, currentColors) ||
        getColorForLabel(labelName, currentColors),
      indexedLabelColor,
      colour,
      matchedLabel?.colour,
      "#6b7280"
    );
  };

  // const detailType = each_image_detail?.type;
  // const detailTaskType = String(
  //   each_image_detail?.task_type || tasktype || ""
  // ).toLowerCase().replace(/[\s-]+/g, "_");
  // const detailAnnotation = each_image_detail?.annotation;
  // const firstAnnotationGroup = Array.isArray(detailAnnotation?.[0])
  //   ? detailAnnotation[0]
  //   : [];
  const hasAnnotationData = Array.isArray(detailAnnotation)
    ? detailAnnotation.some((item) =>
      Array.isArray(item) ? item.length > 0 : item != null
    )
    : detailAnnotation && typeof detailAnnotation === "object"
      ? Object.keys(detailAnnotation).length > 0
      : Boolean(detailAnnotation);
  const currentVideoData = videoFrames?.[each_image_detail?.media_name];
  const currentVideoIntervalValue = Number(
    currentVideoData?.prediction_interval
  );
  const currentVideoInterval =
    Number.isFinite(currentVideoIntervalValue) && currentVideoIntervalValue > 0
      ? Math.floor(currentVideoIntervalValue)
      : 1;
  const currentPredictionFrame =
    Math.floor(currentFrameIndex / currentVideoInterval) * currentVideoInterval;
  const currentVideoPredictionBoxes =
    currentVideoData?.frames?.[currentFrameIndex] ||
    currentVideoData?.frames?.[currentPredictionFrame] ||
    [];
  const hasGroundTruthData =
  (Array.isArray(each_image_detail?.ground_truth)
    ? each_image_detail.ground_truth.length > 0
    : Boolean(each_image_detail?.ground_truth));
  const hasClassificationPredictions =
    detailType === "classification" &&
    Array.isArray(detailAnnotation) &&
    detailAnnotation?.[4] != null &&
    detailAnnotation?.[5] != null;
  const hasSegmentationPredictions =
    detailTaskType === "segmentation" && firstAnnotationGroup.length > 0;
  const hasFaceRecognitionPredictions =
    detailType === "face_recognition" &&
    detailAnnotation &&
    typeof detailAnnotation === "object" &&
    Object.keys(detailAnnotation).length > 0;
  const hasObjectDetectionPredictions =
    (detailTaskType === "object_detection" ||
      (isExperimentImageDetailMode && isExperimentBboxRowList(firstAnnotationGroup))) &&
    firstAnnotationGroup.length > 0;
  const hasKeypointPredictions =
    detailTaskType === "pose_estimation" &&
    Array.isArray(each_image_detail?.label?.[0]) &&
    each_image_detail.label[0].length > 0 &&
    (Array.isArray(each_image_detail?.score) || each_image_detail?.skeleton);
  const hasSuperpointKeypointPredictions =
    isSuperpointKeypointDetection && superpointLandmarkPoints.length > 0;
  const hasDepthPredictions =
    detailTaskType === "depth_estimation" &&
    Array.isArray(each_image_detail?.label) &&
    each_image_detail.label.length > 0;
  const hasImagePredictions =
    each_image_detail?.media_type !== "video" &&
    (
      hasClassificationPredictions ||
      hasSegmentationPredictions ||
      hasFaceRecognitionPredictions ||
      hasObjectDetectionPredictions ||
      hasKeypointPredictions ||
      hasSuperpointKeypointPredictions ||
      hasDepthPredictions
    );
  const objectDetectionPredictionRows = hasObjectDetectionPredictions
    ? (Array.isArray(each_image_detail.annotation?.[0]) &&
      isGroundTruthBBoxRow(each_image_detail.annotation[0])
        ? each_image_detail.annotation
        : each_image_detail.annotation?.[0] || firstAnnotationGroup)
      .map((ann, idx) => ({
        ann,
        idx,
        roundedScore: getRoundedScore(
          typeof ann?.[5] === "number"
            ? ann[5]
            : each_image_detail?.score?.[idx]
        ),
      }))
      .filter(({ ann, roundedScore }) =>
        Boolean(ann) &&
        isClassVisible(getPredictionLabelParts(ann).label) &&
        (isExperimentImageDetailMode || roundedScore >= scoreThreshold)
      )
      .map((row, hoverIndex) => ({ ...row, hoverIndex }))
    : [];
  const visibleObjectDetectionGroundTruthRows =
    objectDetectionGroundTruthRows
      .map((ann, idx) => ({ ann, idx }))
      .filter(({ ann }) =>
        isClassVisible(getGroundTruthFilterLabel(ann))
      )
      .map((row, hoverIndex) => ({ ...row, hoverIndex }));
  const visibleKeypointPredictionItems = hasKeypointPredictions
    ? each_image_detail.label[0]
      .map((labelName, idx) => ({ labelName, idx }))
      .filter(({ labelName }) => isClassVisible(labelName))
    : [];
  const visibleVideoPredictionBoxes = currentVideoPredictionBoxes.filter((box) => {
    const label = typeof box?.label === "string"
      ? box.label.replace(/\s\([\d.]+\)$/, "")
      : box?.label;
    return isClassVisible(label);
  });
  const hasVisibleImagePredictions =
    (hasClassificationPredictions &&
      isClassVisible(each_image_detail?.annotation?.[4])) ||
    (hasSegmentationPredictions &&
      firstAnnotationGroup.some((ann) =>
        isClassVisible(ann?.[0]) &&
        getRoundedScore(ann?.[2]) >= scoreThreshold
      )) ||
    hasFaceRecognitionPredictions ||
    objectDetectionPredictionRows.length > 0 ||
    visibleKeypointPredictionItems.length > 0 ||
    (hasSuperpointKeypointPredictions &&
      superpointLandmarkPoints.some((point) =>
        Number(point?.[2]) >= scoreThreshold
      )) ||
    (hasDepthPredictions &&
      each_image_detail.label.some((labelName) => isClassVisible(labelName)));
  const hasVisibleGroundTruthLabels =
    detailTaskType === "pose_estimation"
      ? keypointGroundTruthLabelItems.some((item) => isClassVisible(item?.name))
      : isShapeGroundTruth
        ? visibleObjectDetectionGroundTruthRows.length > 0
        : detailType === "classification"
          ? classificationGroundTruthItems.some((item) =>
            isClassVisible(item?.name)
          )
          : hasGroundTruthData && !selectedLabelKey && !allLabelsHidden;
  const hasVideoPredictions =
    each_image_detail?.media_type === "video" &&
    visibleVideoPredictionBoxes.length > 0;
  const hasPredictionData =
    hasImagePredictions || hasVideoPredictions || hasAnnotationData;
  const canToggleClassNames =
    detailTaskType !== "segmentation" &&
    detailTaskType !== "depth_estimation" &&
    (hasPredictionData || hasGroundTruthData);
  const canShowLabelActions =
    each_image_detail &&
    detailType !== "face_recognition" &&
    (hasPredictionData || hasGroundTruthData || canToggleClassNames);

  const filteredAnnotations =
    modelName === "facenet"
      ? null
      : each_image_detail?.annotation
        ? (isExperimentImageDetailMode
            ? normalizeAnnotationEpochGroups(each_image_detail.annotation)
            : each_image_detail.annotation
          )?.map((annotationGroup) =>
          Array.isArray(annotationGroup)
            ? annotationGroup.filter((annotation, idx) => {
              const label = getPredictionLabelParts(annotation).label;
              const isVisibleByLabel = isClassVisible(label);

              if (isSuperpointKeypointDetection) return isVisibleByLabel;

              const score = getAnnotationScore(annotation, idx, each_image_detail);


              const roundedScore = getRoundedScore(score);

              const isVisibleByThreshold = roundedScore >= scoreThreshold;


              return isVisibleByLabel && isVisibleByThreshold;
            })
            : []
        )
        : annotations?.annotations?.[
          Object?.keys(annotations?.annotations)?.[0]
        ]?.annotation?.map((annotationGroup) =>
          Array.isArray(annotationGroup)
            ? annotationGroup.filter((annotation, idx) => {
              const label = getPredictionLabelParts(annotation).label;
              const isVisibleByLabel = isClassVisible(label);

              const score = getAnnotationScore(annotation, idx, each_image_detail);
              const roundedScore = getRoundedScore(score);
              const isVisibleByThreshold = roundedScore >= scoreThreshold;

              return isVisibleByLabel && isVisibleByThreshold;
            })
            : []
        );

  // ----------------------- labels scroll --------------------------//

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const container = document.querySelector(".scrolls");
    const updateScrollButtons = () => {
      if (container) {
        const isScrollable = container.scrollWidth > container.clientWidth;
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(
          container.scrollLeft < container.scrollWidth - container.clientWidth
        );
        if (
          !isScrollable ||
          container.scrollWidth < container.clientWidth * 0.9
        ) {
          setCanScrollLeft(false);
          setCanScrollRight(false);
        }
      }
    };

    updateScrollButtons();
    if (container) {
      container.addEventListener("scroll", updateScrollButtons);
      window.addEventListener("resize", updateScrollButtons);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", updateScrollButtons);
        window.removeEventListener("resize", updateScrollButtons);
      }
    };
  }, [annotations]);
  useEffect(() => {
    const container = document.querySelector(".scrolls");
    const updateScrollButtons = () => {
      if (container) {
        const isScrollable = container.scrollWidth > container.clientWidth;
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(
          container.scrollLeft < container.scrollWidth - container.clientWidth
        );
        if (
          !isScrollable ||
          container.scrollWidth < container.clientWidth * 0.9
        ) {
          setCanScrollLeft(false);
          setCanScrollRight(false);
        }
      }
    };

    updateScrollButtons();
    if (container) {
      container.addEventListener("scroll", updateScrollButtons);
      window.addEventListener("resize", updateScrollButtons);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", updateScrollButtons);
        window.removeEventListener("resize", updateScrollButtons);
      }
    };
  }, []);



  const truncateText = (text, maxLength = 15) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const fileNameToDisplay =
    each_image_detail?.media_type === "video"
      ? each_image_detail?.media_name
      : each_image_detail?.image_name ??
      Object.values(annotations?.annotations || {})?.[0]?.image_name;

  const inferenceTime =
    each_image_detail?.time ??
    (each_image_detail?.type === "classification"
      ? each_image_detail?.annotation?.[6]
      : null);

  const aiReasoningSchema = getAiReasoningSchema(each_image_detail);
  const aiReasoningText =
    aiReasoningSchema?.reasoning ??
    aiReasoningSchema?.explanation ??
    aiReasoningSchema?.summary;
  const aiConfidenceAssessment =
    aiReasoningSchema?.confidence_assessment ??
    aiReasoningSchema?.confidenceAssessment;
  const aiFailureModes = normalizeReasoningList(
    aiReasoningSchema?.failure_modes ?? aiReasoningSchema?.failureModes
  );
  const aiLocalSuggestion =
    aiReasoningSchema?.local_suggestion ??
    aiReasoningSchema?.localSuggestion ??
    aiReasoningSchema?.suggestion;
  const hasAiReasoning = Boolean(
    aiReasoningText ||
    aiConfidenceAssessment ||
    aiFailureModes.length ||
    aiLocalSuggestion
  );

  const currentImageKey = filteredImages?.[currentIndex];
  const currentAnnotationDetail =
    (ParentData ? ParentData : annotations)?.annotations?.[currentImageKey];
  const firstAnnotationDetail = Object.values(annotations?.annotations || {})?.[0];
  const selectedImageDimensions = getImageDimensions(each_image_detail);
  const currentAnnotationDimensions = getImageDimensions(currentAnnotationDetail);
  const fallbackImageDimensions = getImageDimensions(firstAnnotationDetail);
  const currentCanvasImageSize = canvasImageSizeByKey?.[currentImageKey] || {};
  const displayImageWidth =
    imagewidth ??
    selectedImageDimensions.width ??
    currentAnnotationDimensions.width ??
    currentCanvasImageSize.width ??
    fallbackImageDimensions.width ??
    "-";
  const displayImageHeight =
    imgheight ??
    selectedImageDimensions.height ??
    currentAnnotationDimensions.height ??
    currentCanvasImageSize.height ??
    fallbackImageDimensions.height ??
    "-";

  const experimentCanvasSrc = useMemo(() => {
    if (!isExperimentImageDetailMode) return "";

    const previewUrl = experimentPreviewItems?.[currentIndex]?.url;
    const previewSrc = previewUrl
      ? getDetailImageSrc({ image_url: previewUrl })
      : "";
    const detailSrc = getDetailImageSrc(each_image_detail);
    const expectedName = getExperimentImageNameAtIndex(
      currentIndex,
      filteredImages[currentIndex]
    );
    const loadedName =
      each_image_detail?.image_name ||
      extractImageNameFromSource(each_image_detail?.image_url);

    if (detailSrc && expectedName && loadedName && expectedName === loadedName) {
      return detailSrc;
    }

    if (detailSrc && !expectedName) {
      return detailSrc;
    }

    return previewSrc || detailSrc;
  }, [
    isExperimentImageDetailMode,
    experimentPreviewItems,
    currentIndex,
    filteredImages,
    each_image_detail,
    getExperimentImageNameAtIndex,
  ]);

  const isExperimentDetailStale = useMemo(() => {
    if (!isExperimentImageDetailMode || !each_image_detail) return false;

    const expectedName = getExperimentImageNameAtIndex(
      currentIndex,
      filteredImages[currentIndex]
    );
    const loadedName =
      each_image_detail?.image_name ||
      extractImageNameFromSource(each_image_detail?.image_url);

    return Boolean(expectedName && loadedName && expectedName !== loadedName);
  }, [
    isExperimentImageDetailMode,
    each_image_detail,
    currentIndex,
    filteredImages,
    getExperimentImageNameAtIndex,
  ]);

  const hasSlideContent =
    (isExperimentImageDetailMode && filteredImages.length > 0) ||
    (typeof annotations?.annotations === "object" &&
      annotations?.annotations !== null &&
      Object.keys(annotations?.annotations).length > 0);

  const isImageEnhancementDetail =
    each_image_detail?.task_type === "image_enhancement";
  const enhancedImageSrc = getDetailImageSrc({
    image_url: each_image_detail?.enhanced_image_url,
  });
  const inputImageSrc = getDetailImageSrc(each_image_detail);


  if (
    (loading && annotations && Object.keys(annotations).length === 0) ||
    result?.detail?.message === "Filter process going on" ||
    apiresponse === true ||
    loader
  ) {
    return (
      <div className="d-flex justify-content-center w-100 h-100">
        <div className="de-width h-100">
          {/* Header */}
          <div className="container-card p-2 mb-3 pe-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              {/* <Skeleton variant="circular" width={32} height={32} /> */}
              <Skeleton variant="text" width={220} height={36} />
            </div>
            {/* Filters row */}
            <div className="d-flex gap-2 align-items-center mb-3">
              <Skeleton variant="rounded" width={40} height={32} />
              {[...Array(18)].map((_, idx) => (
                <Skeleton
                  key={idx}
                  variant="rounded"
                  width={90}
                  height={32}
                />
              ))}
            </div>
          </div>

          <div className="d-a gap-3">
            {/* Main image panel */}
            <div className="" style={{ width: 'calc(100% - 400px)' }}>
              <div className="container-card d-flex justify-content-center w-100">
                <Skeleton
                  variant="rounded"
                  width="60%"
                  height={'calc(100vh - 455px)'}
                />
              </div>

              {/* Bottom thumbnails */}
              <div className="container-card d-flex gap-2 mt-3 overflow-hidden">
                {[...Array(8)].map((_, idx) => (
                  <Skeleton
                    key={idx}
                    variant="rounded"
                    width={210}
                    height={75}
                  />
                ))}
              </div>
            </div>

            {/* Right metadata panel */}
            <div className="mt-0" style={{ width: '400px' }} >
              <div className="container-card p-3">
                {/* File name */}
                <Skeleton variant="text" width="80%" height={48} />

                {/* Metadata section */}
                <Skeleton variant="text" width="50%" height={52} className="mt-3" />
                {[...Array(2)].map((_, idx) => (
                  <Skeleton
                    key={idx}
                    variant="text"
                    width="100%"
                    height={40}
                  />
                ))}

                {/* Toggle buttons */}
                <Skeleton variant="text" width="50%" height={42} className="mt-3" />
                {[...Array(2)].map((_, idx) => (
                  <div
                    key={idx}
                    className="d-flex justify-content-between align-items-center mb-2"
                  >
                    <Skeleton variant="text" width="60%" height={50} />
                    <Skeleton variant="rounded" width={36} height={30} />
                  </div>
                ))}

                {/* Opacity slider */}
                <Skeleton variant="text" width="50%" height={42} className="mt-3" />
                <Skeleton variant="rounded" width="100%" height={42} />

                {/* Performance */}
                <Skeleton variant="text" width="50%" height={42} className="mt-3" />
                <Skeleton variant="text" width="70%" height={50} />

                {/* Predictions */}
                <Skeleton variant="text" width="50%" height={42} className="mt-3" />
                <div className="d-flex justify-content-between">
                  <Skeleton variant="text" width="40%" height={40} />
                  <Skeleton variant="text" width="20%" height={40} />
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="de-width mt-4"
      style={{ position: "relative", zIndex: 0, isolation: "isolate" }}
    >
      <AlertComponent alert={alert} />
      <div
        className={
          showProperties ? "slide-container w-100 container-card  py-2  px-4" : "showproperties-slide-container w-100 container-card  py-2  px-4"
        }
      >
        <div
          className="class-cards-list2 bg-transparent px-0 overflow-hidden"
          style={{
            boxShadow:
              Object.keys(annotations?.annotations || {}).length <= 0 && "none",
          }}
        >
          {showLabels && (
            <>
              {annotations?.annotations && (
                <div className="d-a justify-content-between mb-3 mt-2 flex-wrap">

                  <div className="d-a gap-3">
                    <button className="plain-button" onClick={() => setPredictions(false)} >
                      <BackIcon className="wf-icon" />
                    </button>
                    <h6 className="m-0 p-0 mb-0 fw-bold" style={{ fontWeight: "400" }}>Detail View </h6>
                  </div>

                  {/* Detail View pagination is temporarily hidden.
                  {typeof onDetailPageSync === "function" && (
                    <div className="prediction-detail-page-controls">
                      <button
                        type="button"
                        className="plain-button2 prediction-detail-page-controls__button"
                        onClick={() => fetchSyncedDetailPage(page - 1, "end")}
                        disabled={!canGoSyncedPrevPage}
                        aria-label="Previous images page"
                      >
                        {isFetching && page > 1 ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <FontAwesomeIcon icon={faAngleLeft} />
                        )}
                      </button>
                      <span className="text-muted text-nowrap" style={{ fontSize: 12 }}>
                        Page {page}
                        {totalPages ? ` of ${totalPages}` : ""}
                      </span>
                      <button
                        type="button"
                        className="plain-button2 prediction-detail-page-controls__button"
                        onClick={() => fetchSyncedDetailPage(page + 1, "start")}
                        disabled={!canGoSyncedNextPage}
                        aria-label="Next images page"
                      >
                        {isFetching ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <FontAwesomeIcon icon={faAngleRight} />
                        )}
                      </button>
                    </div>
                  )}
                  */}
                </div>
              )}






              {(tasktype !== "FaceRecognition" && tasktype !== "Segmentation" && tasktype !== "Depth Estimation" && tasktype !== "Image Enhancement") && <div className="d-a gap-3 mb-3">

                <div className="d-a gap-2 badge-border overflow-hidden pe-2" style={{ borderLeft: '3px solid var(--leading-color)' }}>


                  {canUseLabelVisibilityControls && tasktype !== "Depth Estimation" && tasktype !== "Classification" &&
                    <Button color='secndary' className="rounded text-nowrap d-a gap-2" onClick={toggleAllLabelVisibility}>
                      {!allLabelsHidden ? <EyeIcon className="wf-icon-small" /> : <EyeOffIcon className="wf-icon-small" />} All
                    </Button>}



                  {canScrollLeft && (
                    <FontAwesomeIcon
                      icon={faAngleLeft}
                      className="scroll-arraws px-2"
                      onClick={() => {
                        const container = document.querySelector(".scrolls");
                        container.scrollBy({ left: -200, behavior: "smooth" });
                      }}
                    />
                  )}

                  <ul
                    className="p-0 d-flex aligin-items-center scrolls mb-0 gap-3"
                    style={{
                      gap: "10px",
                      width: "100%",
                      overflowX: "scroll",
                      scrollbarWidth: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {classFilterLabels?.map((item, i) => {
                      const labelKey = getClassFilterKey(item.name);
                      const isSelected = Boolean(hiddenLabels[labelKey]);
                      const canToggleLabel =
                        canUseLabelVisibilityControls &&
                        tasktype !== "Classification";
                      const canChangeLabelColor =
                        canUseLabelVisibilityControls &&
                        tasktype !== "pose_estimation" &&
                        tasktype !== "Classification";
                      const itemColor =
                        getColorForLabel(item?.name, currentColors) ||
                        item?.colour ||
                        "#1f8cff";
                      const colorValue = isColorValue(itemColor) ? itemColor : "#1f8cff";
                      const labelTextColor = !canUseLabelVisibilityControls
                        ? "var(--text-color)"
                        : allLabelsHidden
                          ? "lightgray"
                          : isLabelSelected
                            ? isSelected
                              ? "blue"
                              : "lightgray"
                            : "unset";
                      return (
                        <li
                          key={item?.name || i}
                          className="d-flex align-items-center"
                          aria-disabled={!canToggleLabel}
                          style={{
                            padding: "2px 0px",
                            cursor: canToggleLabel ? "pointer" : "default",
                            color: labelTextColor,
                          }}>
                          <div
                            style={{
                              position: "relative",
                              display: "inline-block",
                              margin: 5,
                            }}
                          >
                            <input
                              type="color"
                              value={colorValue}
                              disabled={!canChangeLabelColor}
                              onChange={(e) =>
                                handleColorChange(item?.name, e.target.value)
                              }
                              style={{
                                opacity: 0,
                                width: 16,
                                height: 16,
                                cursor: canChangeLabelColor ? "pointer" : "default",
                                position: "absolute",
                                top: 0,
                                left: 0,
                              }}
                            />
                            <div
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: colorValue,
                                pointerEvents: "none",
                              }}
                            ></div>
                          </div>
                          <span
                            onClick={() => {
                              if (!canToggleLabel) return;
                              toggleLabelVisibility(item.name);
                            }}
                          >
                            {item.name}  {tasktype === "Pose Estimation" ? `(${i+1})` : ""}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  {canScrollRight && (
                    <FontAwesomeIcon
                      icon={faAngleRight}
                      className="scroll-arraws px-2"
                      onClick={() => {
                        const container = document.querySelector(".scrolls");
                        container.scrollBy({ left: 200, behavior: "smooth" });
                      }}
                    />
                  )}

                </div>
              </div>}
            </>
          )}


        </div>
      </div>




      <div className="d-flex align-items-start gap-3 h-100 flex-column flex-md-row">




          <div
            ref={predictionCanvasRef}
            className={`prediction-canvas ${ParentData ? "prediction-canvas--streaming" : ""}${!hasSlideContent ? " prediction-canvas--empty" : ""}`}
            style={{
              width: hasSlideContent ? "calc(100% - 400px)" : "100%",
              height: "calc(100vh - 217px)",
            }}
          >
            <div className="h-100">

              {(hasSlideContent || annotations?.annotations) && !noMatch && (
                <>
                  {hasSlideContent ? (
                    <Slide
                      {...properties}
                      ref={slideRef}
                      autoplay={false}
                      infinite={false}
                      defaultIndex={currentIndex}>
                      {filteredImages.map((each, index) => (
                        <React.Fragment key={filteredImages[index] || index}>
                          {fullImages !== "View Only Images" && (
                            <>
                              <div
                                key={index}
                                className={
                                  queryLength > 3 ? "each-slide-full" : "each-slide"
                                }
                              >
                                {(currentIndex === index ||
                                  (isDicomSequence &&
                                    Math.abs(currentIndex - index) <= 1)) &&
                                  ((each_image_detail !== undefined ||
                                    each_image_detail !== null) &&
                                    task_type !== "audio_classification" ? (
                                    <>
                                      {annotations?.annotations?.[filteredImages[index]]?.media_type === 'video' ? (
                                        <div style={{ width: "100%", height: "calc(100vh - 370px)", objectFit: "contain" }}>
                                          {(() => {
                                            const currentAnnotation = annotations?.annotations?.[filteredImages[index]];
                                            if (!currentAnnotation) return null;

                                            const mediaName = currentAnnotation.media_name;
                                            const videoData = videoFrames?.[mediaName];  // 👈 lookup from parent-provided map

                                            if (!videoData) return null;

                                            return (
                                              <EvaluationVideo
                                                url={videoData.url}
                                                frames={videoData.frames}
                                                predictionInterval={videoData.prediction_interval}
                                                video_fps={videoData.video_fps}
                                                playMode="controls"
                                                onFrameChange={setCurrentFrameIndex}
                                                labelvisible={labelvisible}
                                                opacity={value / 100}
                                                currentColors={currentColors}
                                                showPredictions={pridictions}
                                                landmarks={videoData.landmarksFrames}
                                              />
                                            );
                                          })()}
                                        </div>
                                      ) : (<>
                                        {isImageEnhancementDetail ? (
                                          <div className="image-enhancement-comparison">
                                            <div className="image-enhancement-comparison__item">
                                              <div className="image-enhancement-comparison__label">
                                                Input Image
                                              </div>
                                              {inputImageSrc && (
                                                <ZoomableEnhancementImage
                                                  key={inputImageSrc}
                                                  src={inputImageSrc}
                                                  alt="Input"
                                                  onLoad={(event) =>
                                                    handleCanvasImageSize(each, {
                                                      width: event.currentTarget.naturalWidth,
                                                      height: event.currentTarget.naturalHeight,
                                                    })
                                                  }
                                                />
                                              )}
                                            </div>

                                            <div className="image-enhancement-comparison__item">
                                              <div className="image-enhancement-comparison__label">
                                                Enhanced Image
                                              </div>
                                              {enhancedImageSrc && (
                                                <ZoomableEnhancementImage
                                                  key={enhancedImageSrc}
                                                  src={enhancedImageSrc}
                                                  alt="Enhanced result"
                                                />
                                              )}
                                            </div>
                                          </div>
                                        ) : modelName === "facenet" ? (
                                          <MultiImage
                                            src={
                                              each_image_detail?.image_url
                                                ? each_image_detail?.image_url?.startsWith(
                                                  "https:"
                                                )
                                                  ? each_image_detail?.image_url
                                                  : `data:image/jpeg;base64,${each_image_detail?.image_url}`
                                                : each_image_detail?.image_url?.startsWith(
                                                  "data:"
                                                )
                                                  ? each_image_detail?.image_url
                                                  : {
                                                    src1: each_image_detail?.reference_img_url
                                                      ? each_image_detail?.reference_img_url.startsWith(
                                                        "https:"
                                                      )
                                                        ? each_image_detail?.reference_img_url
                                                        : `data:image/jpeg;base64,${each_image_detail?.reference_img_url}`
                                                      : undefined,

                                                    src2: each_image_detail?.image_2
                                                      ? each_image_detail?.image_2.startsWith(
                                                        "https:"
                                                      )
                                                        ? each_image_detail?.image_2
                                                        : `data:image/jpeg;base64,${each_image_detail?.image_2}`
                                                      : undefined,
                                                  }
                                            }
                                            modelDetails={
                                              typeof each_image_detail
                                                ? each_image_detail
                                                : null
                                            }
                                            selectedIndex={selectedTestIndex}
                                            onReferenceImageSizeChange={(size) => handleCanvasImageSize(each, size)}
                                          ></MultiImage>
                                        ) : (
                                          <div
                                            className={`experiment-detail-canvas-wrap${
                                              currentIndex === index
                                                ? " is-active-dicom-frame"
                                                : ""
                                            }`}
                                          >
                                          <Canvas
                                            key={
                                              isDicomSequence
                                                ? "dicom-sequence-canvas"
                                                : isExperimentImageDetailMode
                                                ? "experiment-detail-canvas"
                                                : filteredImages[currentIndex]
                                            }
                                            labelvisible={labelvisible}
                                            src={
                                              isExperimentImageDetailMode
                                                ? experimentCanvasSrc
                                                : (ParentData ? ParentData : annotations)?.annotations?.[each]?.image_url?.startsWith("http")
                                                ? (ParentData ? ParentData : annotations).annotations[each].image_url
                                                : `data:image/jpeg;base64,${(ParentData ? ParentData : annotations).annotations[each]?.image_url}`
                                            }
                                            modelName={modelName}
                                            epochLength={
                                              isExperimentImageDetailMode
                                                ? experimentEpochLength
                                                : each_image_detail?.total_epochs
                                            }
                                            labelName={
                                              typeof each_image_detail
                                                ?.annotation?.[4] === "object"
                                                ? "-"
                                                : each_image_detail?.annotation?.[4]
                                            }
                                            time={
                                              typeof each_image_detail
                                                ?.annotation?.[6] === "object"
                                                ? each_image_detail.annotation[6].time +
                                                "ms"
                                                : each_image_detail?.annotation?.[6] +
                                                "ms"
                                            }
                                            score={
                                              typeof each_image_detail
                                                ?.annotation?.[5] === "object"
                                                ? each_image_detail?.annotation?.[5]
                                                  ?.score
                                                  ? each_image_detail?.annotation?.[5]
                                                    ?.score
                                                  : "-"
                                                : each_image_detail?.annotation?.[5]
                                            }
                                            propability={
                                              typeof each_image_detail
                                                ?.annotation?.[5] === "object"
                                                ? each_image_detail?.annotation?.[5]
                                                  ? each_image_detail?.annotation?.[5]
                                                  : "-"
                                                : each_image_detail?.annotation?.[5]
                                            }
                                            modelDetails={
                                              typeof each_image_detail
                                                ? each_image_detail
                                                : null
                                            }
                                            epochspacing={
                                              each_image_detail?.predictions_interval
                                            }
                                            annotations={
                                              pridictions &&
                                              !isExperimentDetailStale
                                                ? (each_image_detail?.image_url || each_image_detail?.image)
                                                  ? filteredAnnotations
                                                  : null
                                                : null
                                            }
                                            Groundtruth={
                                              Groundtruth &&
                                              !isExperimentDetailStale
                                                ? (each_image_detail?.image_url || each_image_detail?.image)
                                                  ? filteredGroundtruth
                                                  : null
                                                : null
                                            }
                                            //imageName={each.name}
                                            imageName={
                                              (ParentData ? ParentData : annotations)?.annotations?.[each]?.image_name
                                            }
                                            type={type}
                                            samUrl={samUrl ? samUrl : null}
                                            //count={index + 1}
                                            length={filteredImages.length}
                                            plottype={
                                              each_image_detail?.type
                                                ? each_image_detail?.type
                                                : null
                                            }
                                            taskTypes={
                                              each_image_detail?.task_type
                                                ? each_image_detail?.task_type
                                                : null
                                            }
                                            opacity={value / 100}
                                            currentColors={currentColors}
                                            showProperties={showProperties}
                                            hiddenLabels={hiddenLabels}
                                            selectedLabelKey={selectedLabelKey}
                                            allLabelsHidden={allLabelsHidden}
                                            each_image_detail={each_image_detail}
                                            colours={each_image_detail?.colours}
                                            landmarks={each_image_detail?.landmarks}
                                            hoveredAnnotation={hoveredAnnotation}
                                            pridictions={pridictions}
                                            groundtruth={Groundtruth}
                                            model_task_type={tasktype}
                                            onImageSizeChange={(size) => handleCanvasImageSize(each, size)}
                                            onImageReady={
                                              isExperimentImageDetailMode && !isDicomSequence
                                                ? undefined
                                                : () => {
                                                    if (isDicomSequence) {
                                                      dicomReadyFramesRef.current.add(each);
                                                      const isPendingFrame =
                                                        pendingDicomFrameRef.current === each;
                                                      if (
                                                        !isPendingFrame &&
                                                        index !== currentIndex
                                                      ) {
                                                        return;
                                                      }
                                                      pendingDicomFrameRef.current = null;
                                                    } else if (index !== currentIndex) {
                                                      return;
                                                    }
                                                    setspinner(false);
                                                    setDicomTransitionPreview("");
                                                  }
                                            }
                                            scoreThreshold={scoreThreshold}
                                            zoomControlsPosition={zoomControlsPosition}
                                            onZoomControlsPositionChange={setZoomControlsPosition}
                                            dicomSliderPosition={dicomSliderPosition}
                                            onDicomSliderPositionChange={setDicomSliderPosition}
                                            dicomSliceControl={{
                                              enabled: isDicomSequence,
                                              value: currentDicomGlobalIndex + 1,
                                              total: dicomTotalImages,
                                              loading: isFetching || spinner,
                                              onChange: navigateToDicomSlice,
                                            }}
                                            hideEpochSlider={isExperimentImageDetailMode}
                                            onEpochChange={
                                              isExperimentImageDetailMode
                                                ? handleExperimentEpochChange
                                                : undefined
                                            }
                                            controlledEpoch={
                                              isExperimentImageDetailMode
                                                ? experimentEpoch
                                                : undefined
                                            }

                                          ></Canvas>
                                          {isDicomSequence && dicomTransitionPreview && (
                                            <img
                                              className="dicom-transition-preview"
                                              src={dicomTransitionPreview}
                                              alt="Previous DICOM slice while loading"
                                            />
                                          )}
                                          {isDicomSequence && (isFetching || spinner) && (
                                            <div className="dicom-frame-loading" aria-live="polite">
                                              <FontAwesomeIcon icon={faSpinner} spin />
                                              <span>Loading slice…</span>
                                            </div>
                                          )}
                                          {isExperimentImageDetailMode && !isDicomSequence && spinner && (
                                            <div className="experiment-detail-overlay">
                                              <FontAwesomeIcon icon={faSpinner} spin />
                                            </div>
                                          )}
                                          </div>
                                        )} </>)}
                                    </>
                                  ) : (
                                    <AudioPlayer
                                      specView={specView}
                                      audioUrl={each_image_detail?.audio_url}
                                      spectrogram_url={
                                        each_image_detail?.spectrogram_url
                                      }
                                      waveform_url={each_image_detail?.waveform_url}
                                      task_type={task_type}
                                      epochLength={
                                        isExperimentImageDetailMode
                                          ? experimentEpochLength
                                          : each_image_detail?.total_epochs
                                      }
                                      epochspacing={
                                        each_image_detail?.predictions_interval
                                      }
                                      annotations={
                                        pridictions
                                          ? //</div>? annotations?.annotations?.[each.name]?.annotation
                                          each_image_detail?.annotation
                                          : null
                                      }
                                      Groundtruth={
                                        Groundtruth
                                          ? //</div>? annotations?.annotations[each.name]?.ground_truth
                                          each_image_detail?.ground_truth
                                          : null
                                      }
                                      audioName={each_image_detail?.audio_name}
                                      showProperties={showProperties}
                                      value={value}
                                      frequency_steps={each_image_detail?.freq_steps}
                                      color_bar={each_image_detail?.color_bar}
                                    ></AudioPlayer>
                                  ))}
                              </div>
                            </>
                          )}
                        </React.Fragment>
                      ))}
                    </Slide>
                  ) : (
                    <div className="prediction-empty-state">
                      <ErrorScreen
                        message={
                          type === "dataset_versions" || preview
                            ? "No Data Found or Conflicting Datasets"
                            : "No predictions found"
                        }
                      />
                      {(type === "dataset_versions" || preview) && (
                        <>
                          <div className="d-j-a my-4">
                            <Button color="primary rounded">Add {tag} Data</Button>
                          </div>
                          <div
                            style={{
                              gap: "15px",
                              display: "flex",
                              justifyContent: "center",
                            }}
                            className="mt-3"
                          >
                            <label
                              className="radio-label-data"
                              style={{ cursor: "pointer" }}
                            >
                              <input
                                type="radio"
                                name="options"
                                value="Train"
                                checked={tag === "Train"}
                                onChange={() => (
                                  setTag("Train"),
                                  setSelectStatus(false),
                                  setIsTagSwitch(true),
                                  setShowCanvas(false)
                                )}
                                style={{ cursor: "pointer" }}
                              />
                              Train
                            </label>

                            <label
                              className="radio-label-data"
                              style={{ cursor: "pointer" }}
                            >
                              <input
                                type="radio"
                                name="options"
                                value="Test"
                                checked={tag === "Test"}
                                onChange={() => (
                                  setTag("Test"),
                                  setSelectStatus(false),
                                  setIsTagSwitch(true),
                                  setShowCanvas(false)
                                )}
                                style={{ cursor: "pointer" }}
                              />
                              Test
                            </label>

                            <label
                              className="radio-label-data"
                              style={{ cursor: "pointer" }}
                            >
                              <input
                                type="radio"
                                name="options"
                                value="Val"
                                checked={tag === "Val"}
                                onChange={() => (
                                  setTag("Val"),
                                  setSelectStatus(false),
                                  setIsTagSwitch(true),
                                  setShowCanvas(false)
                                )}
                                style={{ cursor: "pointer" }}
                              />
                              Val
                            </label>
                          </div>
                        </>
                      )}
                      {typeof setPredictions === "function" && (
                        <div className="prediction-empty-state__actions">
                          <Button
                            color="primary"
                            className="rounded px-4"
                            onClick={() => setPredictions(false)}
                          >
                            Go back
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}



              {datasetDeleteUrl && (
                <Confirm
                  url={datasetDeleteUrl}
                  close={() => setDatasetDeleteUrl()}
                  images={selectedImages}
                />
              )}

              <ModalComponent
                modalIsOpen={_id}
                setModalIsOpen={setId}
                modalClass={"p-3" + modalClass}
                component={Component}
              />

              <ReactStrapModal
                open={form?.length > 0}
                close={setFormName}
                path={form}
                images={selectedImages}
              />
            </div>
          </div>



          {hasSlideContent &&
            fullImages !== "View Only Images" &&
            annotations?.annotations &&
            !noMatch && (
            <div className="predictions-info-panel container-card px-0" style={{overflowY:'scroll'}}>

              {/* ================= HEADER ================= */}
              <div className="predictions-info-header px-3 ">
                <span className="predictions-info-filename" title={fileNameToDisplay}>
                  {truncateText(
                    fileNameToDisplay,
                    30
                  )}
                </span>
              </div>

              {/* {isExperimentImageDetailMode && experimentDetailError && (
                <div className="predictions-info-section px-3 pt-3 pb-0">
                  <div className="experiment-detail-error" role="alert">
                    {experimentDetailError}
                  </div>
                </div>
              )} */}

              {/* ================= META DATA ================= */}
              {each_image_detail && (
                <div className="predictions-info-section p-3">
                  <div className="predictions-info-section-title d-a gap-2">
                    <ImagesIcon className="wf-icon-small" /> META DATA
                  </div>

                  <div className="predictions-info-kv">
                    <span>Image Width</span>
                    <span>{displayImageWidth}</span>
                  </div>

                  <div className="predictions-info-kv">
                    <span>Image Height</span>
                    <span>{displayImageHeight}</span>
                  </div>
                </div>
              )}

              {isExperimentImageDetailMode && experimentEpochLength > 1 && (
                <div className="predictions-info-section p-3">
                  <div className="predictions-info-section-title d-a gap-2">
                    EPOCH
                  </div>
                  <div className="d-flex align-items-center gap-2 mt-2">
                    <span className="text-nowrap" style={{ width: 80 }}>
                      Epoch
                    </span>
                    <Input
                      style={{ flex: 1 }}
                      type="range"
                      min={0}
                      max={experimentEpochLength}
                      step={
                        each_image_detail?.predictions_interval > 0
                          ? each_image_detail.predictions_interval
                          : 1
                      }
                      value={experimentEpoch}
                      onChange={(e) => handleExperimentEpochChange(Number(e.target.value))}
                      disabled={spinner}
                    />
                    <span style={{ width: 72, textAlign: "right", whiteSpace: "nowrap" }}>
                      {experimentEpoch} / {experimentEpochLength}
                    </span>
                  </div>
                </div>
              )}

              {/* ================= LABEL ACTIONS ================= */}
              {canShowLabelActions && (
                  <div className="predictions-info-section p-3">
                    <div
                      className="predictions-info-section-header d-a gap-2">
                      <LabelsIcon className="wf-icon-small" /> LABEL ACTIONS
                    </div>


                    {!preview && !noMatch && (
                      <div className="prediction-toggle-list">
                        <div className={`prediction-toggle-row ${tasktype === "Depth Estimation" || tasktype === "Classification" || !hasPredictionData ? "greyed-out" : ""}`}>
                          <span className="toggle-label">Predictions</span>
                          <PredictionToggle
                            label="Predictions"
                            checked={pridictions}
                            disabled={tasktype === "Depth Estimation" || tasktype === "Classification" || !hasPredictionData}
                            onChange={() => setPridictions(prev => !prev)}
                          />
                        </div>
                        {/* } */}

                        {/* Ground Truth toggle */}
                        <div className={`prediction-toggle-row ${!hasGroundTruthData ? "greyed-out" : ""}`}>
                          <span className="toggle-label">Ground Truth</span>
                          <PredictionToggle
                            label="Ground Truth"
                            checked={Groundtruth}
                            disabled={!hasGroundTruthData}
                            onChange={handleChange}
                          />
                        </div>

                        {canToggleClassNames &&
                          <div className={`predictions-info-row d-a ${tasktype === "Depth Estimation" || tasktype === "Classification" ? "greyed-out" : ""}`}>
                            <span>Labels</span>

                            <PredictionToggle
                              label="Labels"
                              checked={labelvisible}
                              disabled={tasktype === "Depth Estimation" || tasktype === "Classification"}
                              onChange={() => setlabelsvisible(prev => !prev)}
                            />
                          </div>}

                      </div>
                    )}


                    {tasktype !== "Depth Estimation" &&
                      (
                        (annotations?.annotations?.[firstkey]?.type !== "classification" &&
                          (modelName === "facenet" || modelName === "restnet50")) ||
                        (each_image_detail?.type !== "classification" &&
                          !each_image_detail?.annotation?.[5]?.score && (
                            <div className="d-flex align-items-center mt-3 gap-2">
                              <span className="text-nowrap" style={{ width: 80 }}>Opacity</span>

                              <Input
                                style={{ flex: 1 }}
                                type="range"
                                value={value}
                                min={0}
                                max={100}
                                step={1}
                                onChange={(e) => setValue(e.target.value)}
                              />

                              <span style={{ width: 50, textAlign: "right" }}>
                                {value}%
                              </span>
                            </div>
                          ))
                      )
                    }



                    {(["Object Detection", "Face Landmark Detection", "KeyPoint Detection", "Pose Estimation", "Segmentation"].includes(tasktype) ||
                      isSuperpointKeypointDetection) && (
                      <div className="d-flex align-items-center mt-3 gap-2">
                        <span className="text-nowrap" style={{ width: 80 }}>
                          Confidence
                        </span>

                        <Input
                          className="score-threshold-slider"
                          style={{
                            flex: 1,
                            "--slider-fill": `${scoreThreshold * 100}%`,
                          }}
                          type="range"
                          value={scoreThreshold}
                          min={0}
                          max={1}
                          step={0.01}
                          onChange={(e) => setScoreThreshold(Number(e.target.value))}
                        />

                        <span style={{ width: 50, textAlign: "right" }}>
                          {scoreThreshold.toFixed(2)}
                        </span>
                      </div>
                    )}


                  </div>
                )}

              {(each_image_detail && (samUrl || bar_url) && inferenceTime) ? (
                <div className="predictions-info-section p-3">
                  <div className="predictions-info-section-header d-a gap-2">
                    <PredictionsIcon className="wf-icon-small" /> PERFORMANCE
                  </div>


                  {(
                    each_image_detail?.time ||
                    (each_image_detail?.type === "classification" &&
                      each_image_detail?.annotation?.[6])
                  ) && (
                      <div className="predictions-info-kv">
                        <span>Inference time</span>
                        <span className="accent">{inferenceTime + "ms"}</span>
                      </div>
                    )}
                </div>
              )
                :
                null
              }

              {/* ================= PREDICTIONS (IMAGE) ================= */}
              {hasImagePredictions && hasVisibleImagePredictions && (
                  <div className="predictions-info-section p-3">
                    <div
                      className="predictions-info-section-header d-a gap-2"
                    >
                      <FontAwesomeIcon icon={faSquare} ></FontAwesomeIcon>
                      PREDICTIONS
                    </div>

                    <div className="predictions-info-table">
                      <div className="predictions-info-table-header">
                        <span>{tasktype === "Depth Estimation" ? "DEPTH RANGE" : "OBJECT"}</span>
                        <span>
                          {isExperimentImageDetailMode && hasObjectDetectionPredictions
                            ? "COORDINATES (x, y, w, h)"
                            : tasktype === "Depth Estimation"
                              ? ""
                              : "SCORE"}
                        </span>
                      </div>

                      {hasClassificationPredictions && (
                        <div className="predictions-info-table-row">
                          <span className="d-a">
                            <span
                              className="dot"
                              style={{
                                backgroundColor: getPredictionColorForRow(
                                  each_image_detail.annotation,
                                  0,
                                  each_image_detail.annotation?.[4]
                                ),
                              }}
                            />
                            {formatDisplayValue(each_image_detail.annotation?.[4])}
                          </span>
                          <span>
                            {Number(each_image_detail.annotation?.[5] ?? 0).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {hasSegmentationPredictions &&
                        each_image_detail.annotation[0]
                          .map((ann, idx) => ({
                            ann,
                            idx,
                            roundedScore: getRoundedScore(ann?.[2]),
                          }))
                          .filter(({ ann, roundedScore }) =>
                            isClassVisible(ann?.[0]) &&
                            roundedScore >= scoreThreshold
                          )
                          .map(({ ann, idx, roundedScore }) => (
                            <div
                              key={idx}
                              className="predictions-info-table-row"
                              onMouseEnter={() => hoverPrediction(idx)}
                              onMouseLeave={() => setHoveredAnnotation(null)}
                            >
                              <span className="d-a">
                                <span
                                  className="dot"
                                  style={{ backgroundColor: ann?.[1] }}
                                />
                                {ann?.[0]}
                              </span>
                              <span>{roundedScore.toFixed(2)}</span>
                            </div>
                          ))}

                      {hasFaceRecognitionPredictions && Object.entries(each_image_detail.annotation).map(
                          ([fileName, data], idx) => (
                            <div
                              key={idx}
                              className="predictions-info-table-row cursor-pointer"
                              onClick={() => {
                                setSelectedTestIndex(null);
                                setTimeout(() => setSelectedTestIndex(idx), 0);
                              }}
                            >
                              <span className="d-a " title={fileName}>
                                {truncateText(fileName.replace(/\.[^/.]+$/, ""), 15)}
                              </span>
                              <span>{Number(data?.similarity).toFixed(4)}</span>
                            </div>
                          )
                        )}



                      {hasObjectDetectionPredictions &&
                        objectDetectionPredictionRows
                          .map(({ ann, idx, hoverIndex, roundedScore }) => {
                            const hasScore =
                              typeof ann?.[5] === "number" ||
                              each_image_detail?.score?.[idx] != null;

                            return (
                              <div
                                key={idx}
                                className={`predictions-info-table-row${hoveredAnnotation?.source === "prediction" && hoveredAnnotation?.index === hoverIndex ? " is-hovered" : ""}`}
                                onMouseEnter={() => hoverPrediction(hoverIndex, ann?.[4])}
                                onMouseLeave={() => setHoveredAnnotation(null)}
                              >
                                <span className="d-a">
                                  <span
                                    className="dot"
                                    style={{
                                      backgroundColor: getPredictionColorForRow(
                                        ann,
                                        idx,
                                        ann?.[4]
                                      ),
                                    }}
                                  />
                                  {formatTrackedBboxLabel(
                                    ann,
                                    ann?.[4],
                                    type === "dataset_versions"
                                  )}
                                </span>
                                <span>
                                  {isExperimentImageDetailMode
                                    ? formatExperimentBboxCoordinates(ann)
                                    : hasScore
                                      ? roundedScore.toFixed(2)
                                      : "-"}
                                </span>
                              </div>
                            );
                          })}

                      {hasKeypointPredictions &&
                        visibleKeypointPredictionItems
                          .map(({ labelName, idx }) => (
                          <div
                            key={idx}
                            className="predictions-info-table-row"
                            onMouseEnter={() => hoverPrediction(idx)}
                            onMouseLeave={() => setHoveredAnnotation(null)}
                          >
                            <span className="d-a">
                              <span
                                className="dot"
                                style={{
                                  backgroundColor: getPredictionColorForRow(
                                    null,
                                    idx,
                                    labelName
                                  ),
                                }}
                              />
                              {labelName}&nbsp;({idx + 1})
                            </span>

                            <span>
                              {Number(each_image_detail?.score?.[idx] ?? 0).toFixed(4)}
                            </span>
                          </div>
                        ))}

                      {hasSuperpointKeypointPredictions &&
                        superpointLandmarkPoints
                          .map((point, idx) => ({
                            point,
                            idx,
                            score: Number(point?.[2]),
                          }))
                          .filter(({ score }) => score >= scoreThreshold)
                          .map(({ idx, score }) => (
                            <div
                              key={idx}
                              className="predictions-info-table-row"
                              onMouseEnter={() => hoverPrediction(idx)}
                              onMouseLeave={() => setHoveredAnnotation(null)}
                            >
                              <span className="d-a">
                                <span
                                  className="dot"
                                  style={{ backgroundColor: "#ff3838" }}
                                />
                                {idx + 1}
                              </span>
                              <span>{score.toFixed(4)}</span>
                            </div>
                          ))}

                      {hasDepthPredictions &&
                        Array.isArray(each_image_detail?.label) &&
                        each_image_detail.label.map((labelName, idx) => {
                          const matchedColor =
                            getPredictionColorForRow(null, idx, labelName);

                          const score =
                            each_image_detail?.score?.[idx] ?? 0;

                          return (
                            <div
                              key={idx}
                              className="predictions-info-table-row"
                              onMouseEnter={() => hoverPrediction(idx)}
                              onMouseLeave={() => setHoveredAnnotation(null)}
                            >
                              <span className="d-a ">
                                <span
                                  className="dot"
                                  style={{ backgroundColor: matchedColor }}
                                />
                                {labelName}
                              </span>

                              {/* <span>{Number(score).toFixed(0)}</span> */}
                            </div>
                          );
                        })}


                    </div>
                  </div>
                )}

              {each_image_detail &&
                each_image_detail?.media_type !== "video" && each_image_detail?.task_type === "depth_estimation" && (
                  <div className="predictions-info-section p-3">
                    <div
                      className="predictions-info-section-header d-a gap-2"
                    >
                      {/* <ActionsIcon className="wf-icon-small" />  */}
                      <FontAwesomeIcon icon={faSquare} ></FontAwesomeIcon>
                      DEPTH STATISTICS
                    </div>
                    {each_image_detail?.task_type === "depth_estimation" &&
                      Array.isArray(each_image_detail?.depth_range) && (
                        <div className="predictions-info-table">
                          <div className="predictions-info-table-row">
                            <span>Min Value</span>
                            <span>
                              {Number(each_image_detail.depth_range[0]).toFixed(6)}
                            </span>
                          </div>

                          <div className="predictions-info-table-row">
                            <span>Max Value</span>
                            <span>
                              {Number(each_image_detail.depth_range[1]).toFixed(6)}
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                )}


              {/* ================= PREDICTIONS (VIDEO) ================= */}
              {hasVideoPredictions &&
                predictionsdataexpand && (
                  <div className="predictions-info-section p-3">
                    <div
                      className="predictions-info-section-header d-a gap-2"
                    >
                      <ActionsIcon className="wf-icon-small" /> PREDICTIONS
                    </div>

                    <div className="predictions-info-table">
                      {visibleVideoPredictionBoxes.map((box, idx) => {
                        let label = box.label;
                        let score = box.score;

                        if (typeof label === "string" && label.includes("(")) {
                          const match = label.match(/^(.*)\s\(([\d.]+)\)$/);
                          if (match) {
                            label = match[1];
                            score = parseFloat(match[2]);
                          }
                        }

                        return (
                          <div key={idx} className="predictions-info-table-row">
                            <span className="d-a">
                              <span
                                className="dot"
                                style={{
                                  backgroundColor:
                                    getColorForLabel(label, currentColors) ||
                                    box.color ||
                                    getPredictionColorForRow(null, idx, label),
                                }}
                              />
                              {label}
                            </span>
                            <span>{score != null ? score.toFixed(2) : "-"}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* ================= GROUND TRUTH ================= */}
              {hasGroundTruthData &&
                hasVisibleGroundTruthLabels &&
                detailTaskType !== "segmentation" && (

                <div className="predictions-info-section p-3">
                  <div
                    className="predictions-info-section-header d-a gap-2"
                  >
                    <PredictionsIcon className="wf-icon-small" /> GROUND TRUTH
                  </div>
                  <div className="predictions-info-table">

                    <div className="predictions-info-section">
                      {isExperimentImageDetailMode &&
                        objectDetectionGroundTruthRows.length > 0 && (
                          <>
                            <div className="predictions-info-table-header">
                              <span>OBJECT</span>
                              <span>COORDINATES (x, y, w, h)</span>
                            </div>
                            {visibleObjectDetectionGroundTruthRows
                              .map(({ ann, idx, hoverIndex }) => {
                              const labelInfo = unpackGroundTruthLabel(
                                ann,
                                groundTruthLabelOptions,
                                currentColors
                              );

                              return (
                                <div
                                  key={idx}
                                  className={`predictions-info-table-row${hoveredAnnotation?.source === "groundtruth" && hoveredAnnotation?.index === hoverIndex ? " is-hovered" : ""}`}
                                  onMouseEnter={() => hoverGroundTruth(hoverIndex, labelInfo?.name)}
                                  onMouseLeave={() => setHoveredAnnotation(null)}
                                >
                                  <span className="d-a">
                                    <span
                                      className="dot"
                                      style={{
                                        backgroundColor:
                                          labelInfo?.colour || ann?.[5] || "#6b7280",
                                      }}
                                    />
                                    {formatTrackedBboxLabel(
                                      ann,
                                      labelInfo?.name || ann?.[4],
                                      type === "dataset_versions"
                                    )}
                                  </span>
                                  <span className="text-muted" style={{ fontSize: 12 }}>
                                    {formatExperimentBboxCoordinates(ann)}
                                  </span>
                                </div>
                              );
                            })}
                          </>
                        )}

                      {!isExperimentImageDetailMode &&
                        groundTruthLabelItems.length > 0 &&
                        groundTruthLabelItems
                          .filter((item) => isClassVisible(item?.name))
                          .map((item, idx) => (
                          <div
                            key={idx}
                            className={`predictions-info-table-row${hoveredAnnotation?.source === "groundtruth" && hoveredAnnotation?.label === item?.name ? " is-hovered" : ""}`}
                            onMouseEnter={() => hoverGroundTruth(null, item?.name)}
                            onMouseLeave={() => setHoveredAnnotation(null)}
                          >
                            <span className="d-a">
                              <span
                                className="dot"
                                style={{
                                  backgroundColor: item?.colour,
                                }}
                              />
                              {item?.name}
                            </span>
                          </div>
                        ))}
                      {classificationGroundTruthItems
                        .filter((item) => isClassVisible(item?.name))
                        .map((item, idx) => (
                        <div
                          key={`${item.name}-${idx}`}
                          className="predictions-info-table-row"
                        >
                          <span className="d-a">
                            <span
                              className="dot"
                              style={{
                                backgroundColor: item?.colour,
                              }}
                            />
                            {item?.name}
                          </span>
                        </div>
                      ))}

                      {each_image_detail?.task_type === "pose_estimation" &&
                        keypointGroundTruthLabelItems
                          .filter((item) => isClassVisible(item?.name))
                          .map((item, idx) => (
                          <div
                            key={idx}
                            className={`predictions-info-table-row${hoveredAnnotation?.source === "groundtruth" && hoveredAnnotation?.index === idx ? " is-hovered" : ""}`}
                            onMouseEnter={() => hoverGroundTruth(idx, item?.name)}
                            onMouseLeave={() => setHoveredAnnotation(null)}
                          >
                            <span className="d-a">
                              <span
                                className="dot"
                                style={{
                                  backgroundColor: item?.colour,
                                }}
                              />
                              {item?.name}&nbsp;({item?.number})
                            </span>


                          </div>
                        ))}
                    </div>

                  </div>
                </div>)}

              {/* ================= AI REASONING ================= */}
              {hasAiReasoning && (
                <div className="predictions-info-section prediction-ai-reasoning p-3">
                  <div className="predictions-info-section-header prediction-ai-reasoning__title d-a gap-2">
                    <CustomisePlanIcon className="wf-icon-small" />
                    AI REASONING
                  </div>

                  <div className="prediction-ai-reasoning__body">
                    {aiReasoningText && (
                      <p className="prediction-ai-reasoning__explanation">
                        {aiReasoningText}
                      </p>
                    )}

                    {aiConfidenceAssessment && (
                      <div className="prediction-ai-reasoning__row">
                        <span>Confidence assessment</span>
                        <span className="prediction-ai-reasoning__confidence">
                          <i className={`prediction-ai-reasoning__dot prediction-ai-reasoning__dot--${getConfidenceTone(aiConfidenceAssessment)}`} />
                          {aiConfidenceAssessment}
                        </span>
                      </div>
                    )}

                    {aiFailureModes.length > 0 && (
                      <div className="prediction-ai-reasoning__row prediction-ai-reasoning__row--top">
                        <span>Potential failure modes</span>
                        <span className="prediction-ai-reasoning__tags">
                          {aiFailureModes.map((mode) => (
                            <span className="prediction-ai-reasoning__tag" key={mode}>
                              {mode}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}

                    {aiLocalSuggestion && (
                      <div className="prediction-ai-reasoning__tip">
                        <FontAwesomeIcon icon={faLightbulb} />
                        <strong>Tip:</strong>
                        <span>{aiLocalSuggestion}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}



        </div>
      <div className="mobile-view-bottom"></div>
    </div>
  );
}
